<template>
  <div class="paper-manage">
    <div class="wrap">
      <div class="page-hd">
        <div class="view-mode-tabs" role="tablist" aria-label="查看方式">
          <button
            type="button"
            class="view-mode-tab"
            role="tab"
            :class="{ 'is-active': viewMode === 'paper' }"
            :aria-selected="viewMode === 'paper' ? 'true' : 'false'"
            @click="switchViewMode('paper')"
          >按试卷查看</button>
          <button
            type="button"
            class="view-mode-tab"
            role="tab"
            :class="{ 'is-active': viewMode === 'ability' }"
            :aria-selected="viewMode === 'ability' ? 'true' : 'false'"
            @click="switchViewMode('ability')"
          >按关键能力细目查看</button>
        </div>
        <div class="stat-chips">
          <div class="stat-chip">
            <div class="num">{{ spaceTotal }}</div>
            <div class="label">研伴空间</div>
          </div>
          <div class="stat-chip">
            <div class="num">{{ paperTotal }}</div>
            <div class="label">已提交试卷</div>
          </div>
        </div>
      </div>

      <div class="page-filters" v-if="viewMode === 'paper'">
        <select
          class="filter-select"
          v-model="spaceFilter"
          @change="onFilterChange"
          aria-label="按研伴空间筛选"
        >
          <option value="">全部研伴空间</option>
          <option v-for="space in spaces" :key="space.id" :value="space.id">{{ space.name }}</option>
        </select>
        <select
          class="filter-select"
          v-model="versionFilter"
          @change="onFilterChange"
          aria-label="按试卷版本筛选"
        >
          <option value="">全部</option>
          <option value="2">过程版本</option>
          <option value="1">终版</option>
        </select>
        <input
          class="search-input"
          type="search"
          v-model.trim="keyword"
          placeholder="搜索任务 / 试卷名称 / 创建人"
          autocomplete="off"
          @input="onKeywordInput"
        />
      </div>

      <div class="layout">
        <aside class="panel catalog-panel">
          <div v-if="viewMode === 'ability'" class="ability-search-bar">
            <input
              class="search-input"
              type="search"
              v-model.trim="keyword"
              placeholder="搜索任务 / 关键能力"
              autocomplete="off"
              @input="onKeywordInput"
            />
          </div>
          <!-- 按试卷查看：任务 → 试卷 -->
          <div class="catalog-list" v-show="viewMode === 'paper'" v-loading="listLoading">
            <div
              v-for="group in groupedPapers"
              :key="group.id"
              class="catalog-group"
              :class="{ 'is-collapsed': collapsedTasks[group.id] }"
            >
              <div class="catalog-group-hd-wrap">
                <button type="button" class="catalog-group-hd" @click="toggleTask(group.id)">
                  <i class="el-icon-arrow-down catalog-group-arrow"></i>
                  <span class="catalog-group-title">{{ group.name }}</span>
                </button>
                <div class="catalog-group-subrow">
                  <span class="catalog-group-meta">组卷任务 · {{ group.papers.length }} 份试卷</span>
                </div>
              </div>
              <div v-show="!collapsedTasks[group.id]" class="catalog-group-body">
                <button
                  v-for="item in group.papers"
                  :key="item.id"
                  type="button"
                  class="catalog-item"
                  :class="{ 'is-active': isSameId(item.id, activePaperId) }"
                  @click="selectPaper(item)"
                >
                  <div class="item-title-row">
                    <div class="item-title">{{ item.title }}</div>
                    <span v-if="hasVersion(item)" class="version-tag" :class="versionClass(item)">{{ versionLabel(item) }}</span>
                    <span v-if="isFinalPaper(item)" class="review-tag" :class="reviewClass(item)">{{ reviewLabel(item) }}</span>
                  </div>
                  <div class="item-meta">
                    <div class="meta-row">
                      <span class="meta-label">研伴空间</span>
                      <span class="meta-value">{{ item.spxName || '—' }}</span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">创建人</span>
                      <span class="meta-value">{{ item.creator || '—' }}</span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">提交人</span>
                      <span class="meta-value">{{ item.author || '—' }}</span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">提交时间</span>
                      <span class="meta-value">{{ formatTime(item.submittedAt) }}</span>
                    </div>
                    <div v-if="isFinalPaper(item)" class="meta-row">
                      <span class="meta-label">专家确认</span>
                      <span class="meta-value">{{ reviewLabel(item) }}<template v-if="reviewCode(item) === 1"> · 可上传答题卡</template></span>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            <div v-if="!listLoading && groupedPapers.length === 0" class="empty">
              <svg viewBox="0 0 72 72" fill="none">
                <rect x="16" y="14" width="40" height="48" rx="4" fill="#e8f0f8" stroke="#b8d0e8" stroke-width="2" />
                <path d="M26 28h20M26 36h16M26 44h12" stroke="#8fb0d0" stroke-width="2" stroke-linecap="round" />
              </svg>
              <div>{{ hasFilters ? '暂无数据' : '暂无已提交试卷' }}</div>
            </div>
          </div>

          <!-- 按关键能力细目查看：任务 → 关键能力 -->
          <div class="catalog-list is-with-search" v-show="viewMode === 'ability'" v-loading="abilityCatalogLoading">
            <div
              v-for="task in abilityTasks"
              :key="'ab-task-' + task.taskId"
              class="catalog-group"
              :class="{ 'is-collapsed': collapsedAbilityTasks[task.taskId] }"
            >
              <button type="button" class="catalog-group-hd" @click="toggleAbilityTask(task.taskId)">
                <i class="el-icon-arrow-down catalog-group-arrow"></i>
                <span class="catalog-group-main">
                  <span class="catalog-group-title">{{ task.taskName || '未命名任务' }}</span>
                  <span class="catalog-group-meta">关键能力 · {{ (task.abilities || []).length }} 项</span>
                </span>
              </button>
              <div v-show="!collapsedAbilityTasks[task.taskId]" class="catalog-group-body">
                <button
                  v-for="ab in (task.abilities || [])"
                  :key="abilityKey(task.taskId, ab.abilityName)"
                  type="button"
                  class="catalog-item"
                  :class="{ 'is-active': activeAbilityKey === abilityKey(task.taskId, ab.abilityName) }"
                  @click="selectAbility(task, ab)"
                >
                  <div class="item-title-row">
                    <div class="item-title">{{ ab.abilityName }}</div>
                  </div>
                  <div class="item-meta">
                    <div class="meta-row">
                      <span class="meta-label">习题数</span>
                      <span class="meta-value">{{ ab.questionCount != null ? ab.questionCount : '—' }} 题</span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">提交版本</span>
                      <span class="meta-value">{{ ab.paperCount != null ? ab.paperCount : '—' }} 份</span>
                    </div>
                    <div class="meta-row">
                      <span class="meta-label">提交空间</span>
                      <span class="meta-value">{{ ab.spaceCount != null ? ab.spaceCount : '—' }} 个</span>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            <div v-if="!abilityCatalogLoading && abilityTasks.length === 0" class="empty">
              <svg viewBox="0 0 72 72" fill="none">
                <rect x="16" y="14" width="40" height="48" rx="4" fill="#e8f0f8" stroke="#b8d0e8" stroke-width="2" />
                <path d="M26 28h20M26 36h16M26 44h12" stroke="#8fb0d0" stroke-width="2" stroke-linecap="round" />
              </svg>
              <div>{{ keyword ? '当前搜索条件下暂无关键能力' : ('当前「' + abilityVersionLabel + '」暂无关键能力细目') }}</div>
            </div>
          </div>
        </aside>

        <section class="panel detail-panel" v-loading="detailLoading || lineageLoading || abilityDetailLoading">
          <div class="detail-tabs" v-if="viewMode === 'paper' && activePaper">
            <button
              type="button"
              class="detail-tab"
              :class="{ 'is-active': activeDetailTab === 'current' }"
              @click="switchDetailTab('current')"
            >当前试卷</button>
            <button
              type="button"
              class="detail-tab"
              :class="{ 'is-active': activeDetailTab === 'lineage' }"
              @click="switchDetailTab('lineage')"
            >过程溯源</button>
            <button type="button" class="task-goal-btn" @click="openTaskGoal">任务目标</button>
          </div>
          <div class="detail-tabs is-ability-version-tabs" v-else-if="viewMode === 'ability'">
            <button
              type="button"
              class="detail-tab"
              :class="{ 'is-active': abilityVersionTab === 'process' }"
              @click="switchAbilityVersionTab('process')"
            >过程版本</button>
            <button
              type="button"
              class="detail-tab"
              :class="{ 'is-active': abilityVersionTab === 'final' }"
              @click="switchAbilityVersionTab('final')"
            >终版</button>
          </div>

          <div class="detail-scroll" v-if="viewMode === 'paper' && activePaper && activeDetailTab === 'current'">
            <div class="paper-detail-layout">
              <div class="paper-card-slot">
              <main class="paper-card" ref="paperMain">
                <header class="paper-head">
                  <h1>{{ activePaper.title }}</h1>
                  <div class="paper-head-meta">
                    <div class="paper-head-info">
                      <span class="paper-head-chip">{{ activePaper.spxName || '—' }}</span>
                      <span class="paper-head-dot" aria-hidden="true"></span>
                      <span class="paper-head-chip">{{ activePaper.creator || '—' }}</span>
                    </div>
                    <span class="paper-tag">{{ flatQuestions.length }}题</span>
                    <span class="paper-tag" v-if="activePaper.grade">{{ activePaper.grade }}</span>
                  </div>
                </header>

                <div v-for="(section, sIdx) in paperSections" :key="'sec-' + sIdx" class="paper-section">
                  <h2 class="section-title">{{ section.name }}</h2>
                  <article
                    v-for="q in section.questions"
                    :key="q.id"
                    :id="questionDomId(q.id)"
                    class="q-item"
                    :class="{ 'is-active': isSameId(q.id, currentNoteQid) }"
                    @click="selectQuestion(q.id)"
                  >
                    <div class="q-stem">
                      <span class="q-no">{{ q.no }}.</span>
                      <span v-if="q.score != null && q.score !== ''" class="q-score">（{{ q.score }}分）</span>
                      <div class="q-stem-html" v-html="q.stem"></div>
                    </div>
                    <div
                      v-if="(q.dimFields || []).length"
                      class="q-dims"
                      :class="{ 'is-collapsed': isDimCollapsed(q.id) }"
                    >
                      <button type="button" class="q-dims-hd" @click.stop="toggleDim(q.id)">
                        <span class="q-dims-title">
                          <i class="el-icon-arrow-down q-dims-arrow"></i>
                          <span>细目表维度标记</span>
                        </span>
                        <span v-if="q.markStatus != null" class="q-dims-status" :class="'is-' + q.markStatus">{{ markStatusLabel(q.markStatus) }}</span>
                      </button>
                      <div v-show="!isDimCollapsed(q.id)" class="q-dims-grid">
                        <div v-for="dim in q.dimFields" :key="dim.key" class="q-dim">
                          <span class="q-dim-label">{{ dim.label }}</span>
                          <div class="q-dim-box">{{ dim.value || '—' }}</div>
                        </div>
                      </div>
                    </div>
                    <div v-show="showAnswer" class="q-extra" data-answer-block>
                      <div class="q-extra-row">
                        <span class="q-extra-label">【答案】</span>
                        <span
                          v-if="q.answerParsed || q.answer"
                          class="q-extra-html"
                          v-html="q.answerParsed || q.answer"
                        ></span>
                        <template v-else>—</template>
                      </div>
                      <div class="q-extra-row">
                        <span class="q-extra-label">【解析】</span>
                        <div class="q-extra-html is-block" v-if="q.analysis" v-html="q.analysis"></div>
                        <template v-else>—</template>
                      </div>
                    </div>
                    <div v-if="notesOf(q.id).length" class="q-note-display">
                      <div
                        v-if="notesOf(q.id).length > 1"
                        class="q-note-display-head"
                      >批注（{{ notesOf(q.id).length }} 条）</div>
                      <div
                        v-for="(note, ni) in notesOf(q.id)"
                        :key="note.id || ('n-' + q.id + '-' + ni)"
                        class="q-note-item"
                      >
                        <div class="q-note-display-meta">
                          <span class="q-note-display-author">{{ note.author || '—' }}</span>
                          <span class="q-note-display-time">{{ formatNoteTime(note.time) }}</span>
                          <span v-if="note.versionName" class="q-note-display-version">{{ note.versionName }}</span>
                        </div>
                        <div class="q-note-display-text" v-html="renderNoteHtml(note.content)"></div>
                      </div>
                    </div>
                  </article>
                </div>
              </main>
              </div>

              <div class="detail-aside">
                <aside class="side-card">
                  <div class="side-card-hd">
                    <h3>题目导航</h3>
                    <button
                      type="button"
                      class="btn-outline"
                      :class="{ 'is-on': showAnswer }"
                      @click="toggleAnswer"
                    >{{ showAnswer ? '隐藏答案解析' : '显示答案解析' }}</button>
                  </div>
                  <div class="toc-list toc-list-grouped">
                    <div
                      v-for="(section, sIdx) in paperSections"
                      :key="'toc-sec-' + sIdx"
                      class="toc-person-group"
                    >
                      <div class="toc-person-name">{{ section.name }}</div>
                      <div class="toc-person-items">
                        <button
                          v-for="q in section.questions"
                          :key="'toc-' + q.id"
                          type="button"
                          class="toc-item"
                          :class="{
                            'is-current': isSameId(q.id, currentNoteQid),
                            'has-note': hasAnyNote(q.id)
                          }"
                          @click.stop="selectQuestion(q.id, true)"
                        >{{ q.no }}</button>
                      </div>
                    </div>
                  </div>
                  <div v-if="isFinalPaper(activePaper)" class="paper-review-bar" :class="'is-' + reviewKey(activePaper)">
                    <div class="paper-review-title">
                      <span>终版可行性确认</span>
                      <span class="review-tag" :class="reviewClass(activePaper)">{{ reviewLabel(activePaper) }}</span>
                    </div>
                    <div class="paper-review-desc">{{ reviewDesc(activePaper) }}</div>
                    <div v-if="reviewCode(activePaper) === 2 && activePaper.reviewReason" class="paper-review-reason">
                      <span>原因：</span>{{ activePaper.reviewReason }}
                    </div>
                    <div class="paper-review-actions">
                      <button type="button" class="btn-approve" :disabled="reviewSaving || reviewLocked(activePaper)" @click="confirmFeasible">{{ reviewCode(activePaper) === 1 ? '已确认可行' : '确认可行' }}</button>
                      <button type="button" class="btn-reject" :disabled="reviewSaving || reviewLocked(activePaper)" @click="openReject">{{ reviewCode(activePaper) === 2 ? '已判定不可行' : '不可行' }}</button>
                    </div>
                  </div>
                  <div class="side-stat">共 {{ flatQuestions.length }} 题<span v-if="totalScore"> · 满分 {{ totalScore }} 分</span></div>
                </aside>

                <aside class="note-panel">
                  <div class="note-scope-tabs" role="tablist" aria-label="批注范围">
                    <button
                      type="button"
                      class="note-scope-tab"
                      :class="{ 'is-active': noteScope === 'question' }"
                      @click="switchNoteScope('question')"
                    >本题批注</button>
                    <button
                      type="button"
                      class="note-scope-tab"
                      :class="{ 'is-active': noteScope === 'paper' }"
                      @click="switchNoteScope('paper')"
                    >整卷批注</button>
                  </div>
                  <div v-show="noteScope === 'question'" class="note-panel-editor">
                    <div class="note-panel-head">
                      <span class="note-panel-title">第 {{ currentNoteNo || 1 }} 题 · 我的批注</span>
                      <span class="note-panel-status" :class="{ 'is-saved': noteStatusSaved }">{{ noteStatusText }}</span>
                    </div>
                    <textarea
                      class="note-panel-input"
                      v-model="noteDraft"
                      rows="8"
                      placeholder="填写本题批注、易错点或讲解备注…"
                      :disabled="currentNoteQid == null || currentNoteQid === '' || noteSaving"
                      @input="onNoteInput"
                    ></textarea>
                    <div class="note-panel-actions">
                      <button
                        type="button"
                        class="btn-outline"
                        :disabled="noteSaving || currentNoteQid == null || currentNoteQid === ''"
                        @click="clearCurrentNote"
                      >清除</button>
                      <button
                        type="button"
                        class="btn-confirm"
                        :disabled="!canConfirmNote || noteSaving"
                        @click="confirmCurrentNote"
                      >{{ noteSaving ? '保存中...' : '确认批注' }}</button>
                    </div>
                  </div>
                  <div v-show="noteScope === 'paper'" class="note-panel-editor note-panel-paper">
                    <div class="note-panel-head">
                      <span class="note-panel-title">整卷点评</span>
                      <span class="note-panel-status" :class="{ 'is-saved': paperReviews.length > 0 }">{{ paperReviewStatusText }}</span>
                    </div>
                    <div v-if="paperReviewLoading" class="paper-review-empty">加载中…</div>
                    <div v-else-if="!paperReviews.length" class="paper-review-empty">暂无整卷点评</div>
                    <div v-else class="paper-review-list">
                      <div
                        v-for="item in paperReviews"
                        :key="'pr-' + item.id"
                        class="paper-review-item"
                        :class="{ 'is-mine': isMyNote(item), 'is-editing': isSameId(paperReviewEditingId, item.id) }"
                      >
                        <div class="paper-review-meta">
                          <span class="paper-review-author">{{ item.author || '—' }}</span>
                          <span class="paper-review-time">{{ formatNoteTime(item.time) }}</span>
                        </div>
                        <template v-if="isSameId(paperReviewEditingId, item.id)">
                          <textarea
                            class="note-panel-input paper-review-edit-input"
                            v-model="paperReviewEditDraft"
                            rows="4"
                            :disabled="noteSaving"
                          ></textarea>
                          <div class="paper-review-item-actions">
                            <button type="button" class="btn-outline" :disabled="noteSaving" @click="cancelEditPaperReview">取消</button>
                            <button
                              type="button"
                              class="btn-confirm"
                              :disabled="noteSaving || !(paperReviewEditDraft || '').trim()"
                              @click="saveEditPaperReview"
                            >{{ noteSaving ? '保存中...' : '保存' }}</button>
                          </div>
                        </template>
                        <template v-else>
                          <div class="paper-review-text" v-html="renderNoteHtml(item.content)"></div>
                          <div v-if="isMyNote(item)" class="paper-review-item-actions">
                            <button type="button" class="btn-text" :disabled="noteSaving" @click="startEditPaperReview(item)">修改</button>
                            <button type="button" class="btn-text is-danger" :disabled="noteSaving" @click="deletePaperReview(item)">删除</button>
                          </div>
                        </template>
                      </div>
                    </div>
                    <div class="paper-review-compose">
                      <div class="note-panel-head">
                        <span class="note-panel-title">新增点评</span>
                      </div>
                      <textarea
                        class="note-panel-input paper-review-compose-input"
                        v-model="paperNoteDraft"
                        rows="5"
                        placeholder="填写对本卷整体评价、结构建议或综合备注…"
                        :disabled="!activePaper || noteSaving || !!paperReviewEditingId"
                        @input="onPaperNoteInput"
                      ></textarea>
                      <div class="note-panel-actions">
                        <button
                          type="button"
                          class="btn-outline"
                          :disabled="noteSaving || !(paperNoteDraft || '').trim() || !!paperReviewEditingId"
                          @click="clearPaperNoteDraft"
                        >清除</button>
                        <button
                          type="button"
                          class="btn-confirm"
                          :disabled="!canConfirmPaperNote || noteSaving || !!paperReviewEditingId"
                          @click="confirmPaperNote"
                        >{{ noteSaving ? '保存中...' : '确认点评' }}</button>
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>

          <div class="detail-scroll lineage-scroll" v-else-if="viewMode === 'paper' && activePaper && activeDetailTab === 'lineage'">
            <div class="lineage-wrap" v-if="lineageData">
              <div class="lineage-summary">
                <div class="lineage-summary-title">{{ lineageData.selectedMainTitle || activePaper.title || '—' }}</div>
                <div class="lineage-summary-meta">
                  <span>创建人：<strong>{{ lineageData.creatorUserName || '—' }}</strong></span>
                  <span v-if="lineageData.taskName">任务：<strong>{{ lineageData.taskName }}</strong></span>
                  <span v-if="lineageData.spxName">空间：<strong>{{ lineageData.spxName }}</strong></span>
                  <span>历经 <strong>{{ lineageStats.versionCount }}</strong> 个版本</span>
                  <span v-if="lineageData.cutoffSendTime">截止发送：<strong>{{ formatTime(lineageData.cutoffSendTime) }}</strong></span>
                </div>
                <div class="lineage-summary-stats">
                  <span>当前版本 <strong>{{ lineageStats.currentCount }}</strong> 题</span>
                  <span>溯源合计 <strong>{{ lineageStats.mergedCount }}</strong> 题</span>
                  <span class="is-add">新增 <strong>{{ lineageStats.addedCount }}</strong></span>
                  <span class="is-del">删除 <strong>{{ lineageStats.removedCount }}</strong></span>
                  <span>无调整 <strong>{{ lineageStats.keptCount }}</strong></span>
                </div>
              </div>

              <div class="lineage-timeline" v-if="lineageVersions.length">
                <template v-for="(v, idx) in lineageVersions">
                  <div
                    :key="'ln-' + (v.paperId || idx)"
                    class="lineage-node"
                    :class="{ 'is-current': !!v.selected }"
                  >
                    <div class="lineage-node-label">
                      <span>{{ v.versionLabel || ('第 ' + (v.versionNo || idx + 1) + ' 版') }}</span>
                      <span class="lineage-node-time">{{ formatTimeToMinute(v.versionTime) }}</span>
                    </div>
                    <div class="lineage-node-meta">
                      {{ v.questionCount != null ? v.questionCount : '—' }} 题
                      <template v-if="v.mainTitle"> · {{ v.mainTitle }}</template>
                    </div>
                  </div>
                  <span
                    v-if="idx < lineageVersions.length - 1"
                    :key="'la-' + idx"
                    class="lineage-arrow"
                    aria-hidden="true"
                  >→</span>
                </template>
              </div>

              <section class="lineage-section">
                <div class="lineage-section-hd">
                  <div class="lineage-section-title">试卷习题</div>
                  <span class="lineage-count">{{ lineageQuestions.length }}</span>
                </div>
                <article
                  v-for="(q, qi) in lineageQuestions"
                  :key="'lq-' + (q.questionId || q.thirdQuestionId || qi)"
                  class="lineage-q"
                  :class="lineageCardClass(q.status)"
                >
                  <span class="change-tag" :class="lineageTagClass(q.status)">{{ q.statusLabel || lineageStatusLabel(q.status) }}</span>
                  <div class="lineage-q-hd">
                    <span class="lineage-q-no">{{ q.displayNo != null ? q.displayNo : '—' }}.</span>
                    <span v-if="q.status === 'removed' && q.versionLabel" class="lineage-q-verno" title="该题所在版本">{{ q.versionLabel }}</span>
                    <span class="lineage-q-type">
                      {{ q.typeName || q.partName || '试题' }}
                      · {{ q.totalScore != null && q.totalScore !== '' ? q.totalScore + ' 分' : '—' }}
                    </span>
                  </div>
                  <div class="lineage-q-stem" v-html="q.stem || ''"></div>
                  <div v-if="(q.dimFields || []).length" class="q-dims lineage-dims-block">
                    <div class="q-dims-hd lineage-dims-hd">
                      <span class="q-dims-title">
                        <span>细目表维度标记</span>
                        <span v-if="q.markStatus != null" class="q-dims-status" :class="'is-' + q.markStatus">{{ markStatusLabel(q.markStatus) }}</span>
                      </span>
                    </div>
                    <div class="q-dims-grid">
                      <div v-for="dim in q.dimFields" :key="'ld-' + dim.key" class="q-dim">
                        <span class="q-dim-label">{{ dim.label }}</span>
                        <div class="q-dim-box">{{ dim.value || '—' }}</div>
                      </div>
                    </div>
                  </div>
                  <div v-if="(q.notes || []).length" class="lineage-mentor-notes">
                    <div class="lineage-mentor-title">批注（{{ q.notes.length }}）</div>
                    <div
                      v-for="(note, ni) in q.notes"
                      :key="note.id || ('ln-note-' + qi + '-' + ni)"
                      class="q-note-item"
                    >
                      <div class="q-note-display-meta">
                        <span class="q-note-display-author">{{ note.author || '—' }}</span>
                        <span class="q-note-display-time">{{ formatNoteTime(note.time) }}</span>
                        <span v-if="note.versionName" class="q-note-display-version">{{ note.versionName }}</span>
                      </div>
                      <div class="q-note-display-text" v-html="renderNoteHtml(note.content)"></div>
                    </div>
                  </div>
                  <div v-else-if="q.noteCount" class="lineage-mentor-notes">
                    <div class="lineage-mentor-title">批注（{{ q.noteCount }}）</div>
                    <div class="q-note-item">
                      <div class="q-note-display-text sec-muted">批注内容暂未返回</div>
                    </div>
                  </div>
                </article>
                <div v-if="!lineageQuestions.length" class="lineage-empty-hint">暂无习题</div>
              </section>
            </div>
            <div v-else class="lineage-empty-hint" style="padding: 40px 16px; text-align: center;">
              {{ lineageLoading ? '加载溯源中...' : (lineageError || '暂无溯源数据') }}
            </div>
          </div>

          <!-- 关键能力详情 -->
          <div class="detail-scroll" v-else-if="viewMode === 'ability' && activeAbilityName">
            <div class="paper-detail-layout">
              <div class="paper-card-slot">
                <main class="paper-card" ref="abilityPaperMain">
                  <header class="paper-head">
                    <h1>{{ activeAbilityName }}</h1>
                    <div class="ability-head-meta">
                      <span>版本：<strong>{{ abilityVersionLabel }}</strong></span>
                      <span>试题数量：<strong>{{ abilityStats.questionCount }}</strong></span>
                      <span>出题人数：<strong>{{ abilityStats.authorCount }}</strong></span>
                      <span>提交版本：<strong>{{ abilityStats.paperCount }}</strong> 份</span>
                      <span v-if="abilityDetail.taskName">任务：<strong>{{ abilityDetail.taskName }}</strong></span>
                    </div>
                  </header>

                  <div v-if="!abilityDetailLoading && !abilityGroups.length" class="detail-placeholder" style="min-height:180px">
                    <div>当前「{{ abilityVersionLabel }}」下，该关键能力暂无习题</div>
                  </div>

                  <div
                    v-for="(group, gIdx) in abilityGroups"
                    :key="'abg-' + (group.paperId || gIdx)"
                    class="ability-source-block"
                  >
                    <div class="ability-source-hd">
                      <div class="ability-source-main">
                        <div class="ability-source-title">
                          <span class="ability-field">
                            <span class="ability-field-label">提交人：</span>{{ group.creatorUserName || '—' }}
                          </span>
                          <span class="ability-field">
                            <span class="ability-field-label">空间：</span>{{ group.spxName || '—' }}
                          </span>
                        </div>
                        <div class="ability-source-meta">
                          <span class="ability-field">
                            <span class="ability-field-label">试卷：</span>{{ group.mainTitle || '未命名试卷' }}
                          </span>
                          <span class="ability-field">
                            <span class="ability-field-label">提交时间：</span>{{ formatTimeToMinute(group.sendTime) }}
                          </span>
                        </div>
                      </div>
                      <div class="ability-source-count">{{ (group.questions || []).length }} 题</div>
                    </div>
                    <article
                      v-for="q in (group.questions || [])"
                      :key="'abq-' + q.id"
                      :id="questionDomId(q.id)"
                      class="q-item"
                      :class="{ 'is-active': isSameId(q.id, currentNoteQid) }"
                      @click="selectQuestion(q.id)"
                    >
                      <div class="q-stem">
                        <span class="q-no">{{ q.no }}.</span>
                        <span v-if="q.score != null && q.score !== ''" class="q-score">（{{ q.score }}分）</span>
                        <div class="q-stem-html" v-html="q.stem"></div>
                      </div>
                      <div class="q-source-bar">
                        <span class="q-source-item">创建人：<strong>{{ group.creatorUserName || '—' }}</strong></span>
                        <span class="q-source-item">所属空间：<strong>{{ group.spxName || '—' }}</strong></span>
                      </div>
                      <div
                        v-if="(q.dimFields || []).length"
                        class="q-dims"
                        :class="{ 'is-collapsed': isDimCollapsed(q.id) }"
                      >
                        <button type="button" class="q-dims-hd" @click.stop="toggleDim(q.id)">
                          <span class="q-dims-title">
                            <i class="el-icon-arrow-down q-dims-arrow"></i>
                            <span>细目表维度标记</span>
                          </span>
                          <span v-if="q.markStatus != null" class="q-dims-status" :class="'is-' + q.markStatus">{{ markStatusLabel(q.markStatus) }}</span>
                        </button>
                        <div v-show="!isDimCollapsed(q.id)" class="q-dims-grid">
                          <div v-for="dim in q.dimFields" :key="dim.key" class="q-dim">
                            <span class="q-dim-label">{{ dim.label }}</span>
                            <div class="q-dim-box">{{ dim.value || '—' }}</div>
                          </div>
                        </div>
                      </div>
                      <div v-show="showAnswer" class="q-extra" data-answer-block>
                        <div class="q-extra-row">
                          <span class="q-extra-label">【答案】</span>
                          <span
                            v-if="q.answerParsed || q.answer"
                            class="q-extra-html"
                            v-html="q.answerParsed || q.answer"
                          ></span>
                          <template v-else>—</template>
                        </div>
                        <div class="q-extra-row">
                          <span class="q-extra-label">【解析】</span>
                          <div class="q-extra-html is-block" v-if="q.analysis" v-html="q.analysis"></div>
                          <template v-else>—</template>
                        </div>
                      </div>
                      <div v-if="notesOf(q.id).length" class="q-note-display">
                        <div
                          v-for="(note, ni) in notesOf(q.id)"
                          :key="note.id || ('abn-' + q.id + '-' + ni)"
                          class="q-note-item"
                        >
                          <div class="q-note-display-meta">
                            <span class="q-note-display-author">{{ note.author || '—' }}</span>
                            <span class="q-note-display-time">{{ formatNoteTime(note.time) }}</span>
                            <span v-if="note.versionName" class="q-note-display-version">{{ note.versionName }}</span>
                          </div>
                          <div class="q-note-display-text" v-html="renderNoteHtml(note.content)"></div>
                        </div>
                      </div>
                    </article>
                  </div>
                </main>
              </div>

              <div class="detail-aside">
                <aside class="side-card">
                  <div class="side-card-hd">
                    <h3>题目导航</h3>
                    <button
                      type="button"
                      class="btn-outline"
                      :class="{ 'is-on': showAnswer }"
                      @click="toggleAnswer"
                    >{{ showAnswer ? '隐藏答案解析' : '显示答案解析' }}</button>
                  </div>
                  <div class="toc-list toc-list-grouped">
                    <div
                      v-for="(group, gIdx) in abilityGroups"
                      :key="'toc-g-' + (group.paperId || gIdx)"
                      class="toc-person-group"
                    >
                      <div class="toc-person-name">{{ group.creatorUserName || '—' }}</div>
                      <div class="toc-person-items">
                        <button
                          v-for="q in (group.questions || [])"
                          :key="'ab-toc-' + q.id"
                          type="button"
                          class="toc-item"
                          :class="{
                            'is-current': isSameId(q.id, currentNoteQid),
                            'has-note': hasAnyNote(q.id)
                          }"
                          @click.stop="selectQuestion(q.id, true)"
                        >{{ q.no }}</button>
                      </div>
                    </div>
                  </div>
                  <div class="side-stat">
                    试题数量 {{ abilityStats.questionCount }} · 出题人数 {{ abilityStats.authorCount }}
                  </div>
                </aside>

                <aside class="note-panel">
                  <div class="note-panel-editor">
                    <div class="note-panel-head">
                      <span class="note-panel-title">第 {{ currentNoteNo || 1 }} 题 · 我的批注</span>
                      <span class="note-panel-status" :class="{ 'is-saved': noteStatusSaved }">{{ noteStatusText }}</span>
                    </div>
                    <textarea
                      class="note-panel-input"
                      v-model="noteDraft"
                      rows="8"
                      placeholder="填写本题批注、易错点或讲解备注…"
                      :disabled="currentNoteQid == null || currentNoteQid === '' || noteSaving"
                      @input="onNoteInput"
                    ></textarea>
                    <div class="note-panel-actions">
                      <button
                        type="button"
                        class="btn-outline"
                        :disabled="noteSaving || currentNoteQid == null || currentNoteQid === ''"
                        @click="clearCurrentNote"
                      >清除</button>
                      <button
                        type="button"
                        class="btn-confirm"
                        :disabled="!canConfirmNote || noteSaving"
                        @click="confirmCurrentNote"
                      >{{ noteSaving ? '保存中...' : '确认批注' }}</button>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>

          <div v-else class="detail-placeholder">
            <svg viewBox="0 0 80 80" fill="none">
              <rect x="18" y="12" width="44" height="56" rx="4" fill="#e8f0f8" stroke="#b8d0e8" stroke-width="2" />
              <path d="M28 28h24M28 38h20M28 48h14" stroke="#8fb0d0" stroke-width="2" stroke-linecap="round" />
            </svg>
            <div>
              {{
                (detailLoading || abilityDetailLoading || abilityCatalogLoading)
                  ? '加载中...'
                  : (viewMode === 'ability' ? '请从左侧目录选择一项关键能力查看' : '请从左侧目录选择一套试卷查看')
              }}
            </div>
          </div>
        </section>
      </div>
    </div>

    <el-dialog
      :title="'任务目标 · ' + (activePaper && activePaper.taskName ? activePaper.taskName : '未命名任务')"
      :visible.sync="taskGoalVisible"
      width="560px"
      append-to-body
      custom-class="task-goal-dialog"
    >
      <div v-loading="taskGoalLoading" class="task-goal-body">
        <div v-for="field in taskGoalFields" :key="field.key" class="task-goal-block">
          <div class="task-goal-label">{{ field.label }}</div>
          <div class="task-goal-text" :class="{ 'is-empty': !String((taskGoal && taskGoal[field.key]) || '').trim() }">{{ taskGoalText(field.key) }}</div>
        </div>
      </div>
      <span slot="footer">
        <el-button type="primary" size="small" @click="taskGoalVisible = false">知道了</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="判定不可行"
      :visible.sync="rejectVisible"
      width="460px"
      append-to-body
      :close-on-click-modal="false"
    >
      <p class="reject-reason-tip">请填写具体原因。判定不可行后，学院端暂不能上传答题卡，且不能再改。</p>
      <el-input
        v-model="rejectReason"
        type="textarea"
        :rows="5"
        maxlength="500"
        show-word-limit
        placeholder="例如：题量偏少 / 知识点覆盖不足 / 难度梯度不合理"
      ></el-input>
      <span slot="footer">
        <el-button @click="rejectVisible = false">取消</el-button>
        <el-button type="danger" :loading="reviewSaving" @click="submitReject">确认不可行</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { renderNoteHtml } from './utils/renderNoteHtml'
import 'katex/dist/katex.min.css'

/** 固定表头（specMode=0）五项 */
const FIXED_DIM_FIELDS = [
  { key: 'knowledgeName', label: '知识点', field: 'knowledgeName', builtinKey: 'knowledge' },
  { key: 'keyAbilityName', label: '关键能力', field: 'keyAbilityName', builtinKey: 'keyAbility' },
  { key: 'subjectAbilityName', label: '学科能力', field: 'subjectAbilityName', builtinKey: 'subjectAbility' },
  { key: 'requirement', label: '考察要求', field: 'requirement', builtinKey: 'requirement' },
  { key: 'objective', label: '测评目标', field: 'objective', builtinKey: 'objective' }
]

/** 专家查看页不展示的细目列（质量水平 / 分值 / 得分率） */
const HIDDEN_DIM_LABELS = ['学业质量水平', '分值', '预计得分率', '实际得分率']

function isHiddenDimLabel(label) {
  return HIDDEN_DIM_LABELS.includes(String(label || '').trim())
}

const BUILTIN_FIELD_BY_KEY = {
  knowledge: 'knowledgeName',
  keyAbility: 'keyAbilityName',
  subjectAbility: 'subjectAbilityName',
  requirement: 'requirement',
  objective: 'objective'
}

function normalizeSpecMode(value) {
  return Number(value) === 1 ? 1 : 0
}

/** 接口可能回字符串或数组，统一成展示文案 */
function formatDimValue(value) {
  if (value == null) return ''
  if (Array.isArray(value)) {
    return value
      .map(item => (item == null ? '' : String(item).trim()))
      .filter(Boolean)
      .join('、')
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch (e) {
      return ''
    }
  }
  return String(value).trim()
}

function normalizeCustomMarks(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out = {}
  Object.keys(raw).forEach(key => {
    const text = formatDimValue(raw[key])
    if (key != null && String(key).trim() !== '') out[String(key)] = text
  })
  return out
}

export default {
  name: 'PaperManage',
  data() {
    return {
      spaces: [],
      allPapers: [],
      spaceFilter: '',
      versionFilter: '',
      keyword: '',
      keywordTimer: null,
      listLoading: false,
      detailLoading: false,
      activePaperId: null,
      activePaper: null,
      collapsedTasks: {},
      dimCollapsed: {},
      flatQuestions: [],
      paperSections: [],
      /** 当前试卷细目形态：0 固定五项 / 1 按返回列动态展示 */
      paperSpecMode: 0,
      paperSpecColumns: [],
      showAnswer: false,
      notesMap: {},
      noteSaving: false,
      currentNoteQid: '',
      currentNoteNo: 0,
      noteDraft: '',
      confirmedDraft: '',
      noteDirty: false,
      /** question | paper：本题批注 / 整卷点评 */
      noteScope: 'question',
      /** 整卷点评列表（多人多条，接口 /examPaper/review/list） */
      paperReviews: [],
      paperReviewLoading: false,
      paperNoteDraft: '',
      paperNoteDirty: false,
      paperReviewEditingId: null,
      paperReviewEditDraft: '',
      taskGoalVisible: false,
      taskGoalLoading: false,
      taskGoal: {
        overall: '',
        knowledge: '',
        ability: '',
        focus: ''
      },
      taskGoalFields: [
        { key: 'overall', label: '总体目标' },
        { key: 'knowledge', label: '知识目标' },
        { key: 'ability', label: '能力目标' },
        { key: 'focus', label: '考查重点与预期产出' }
      ],
      rejectVisible: false,
      rejectReason: '',
      reviewSaving: false,
      activeDetailTab: 'current',
      lineageLoading: false,
      lineageData: null,
      lineageError: '',
      lineagePaperKey: null,
      viewMode: 'paper',
      abilityVersionTab: 'process',
      abilityCatalogLoading: false,
      abilityDetailLoading: false,
      abilityTasks: [],
      collapsedAbilityTasks: {},
      activeAbilityTaskId: null,
      activeAbilityName: '',
      abilityDetail: {},
      abilityGroups: [],
      abilityFlatQuestions: [],
      abilitySpecMode: 0,
      abilitySpecColumns: [],
    }
  },
  computed: {
    ...mapState({
      userInfo: state => state.user.userInfo,
      currentProject: state => state.user.currentProject
    }),
    currentUserId() {
      const info = this.userInfo || {}
      return info.id != null ? info.id : info.userId
    },
    currentAuthor() {
      const info = this.userInfo || {}
      return info.realName || info.nickname || info.name || '专家'
    },
    spaceTotal() {
      return this.spaces.length
    },
    paperTotal() {
      return this.allPapers.length
    },
    hasFilters() {
      return Boolean(this.spaceFilter || this.versionFilter || this.keyword)
    },
    filteredPapers() {
      const kw = (this.keyword || '').trim().toLowerCase()
      return this.allPapers.filter(item => {
        if (this.spaceFilter !== '' && this.spaceFilter != null && !this.isSameId(item.spaceId, this.spaceFilter)) {
          return false
        }
        if (this.versionFilter === '1' && !this.isFinalPaper(item)) return false
        if (this.versionFilter === '2' && !this.isProcessPaper(item)) return false
        if (kw) {
          const hay = [item.taskName, item.title, item.creator, item.spxName].join(' ').toLowerCase()
          if (hay.indexOf(kw) < 0) return false
        }
        return true
      })
    },
    papersByTask() {
      const order = []
      const map = {}
      this.filteredPapers.forEach(item => {
        const tid = item.taskId != null && item.taskId !== '' ? String(item.taskId) : '_unassigned'
        if (!map[tid]) {
          map[tid] = {
            id: tid,
            name: item.taskName || '未关联组卷任务',
            papers: []
          }
          order.push(tid)
        }
        map[tid].papers.push(item)
      })
      return order.map(id => map[id])
    },
    groupedPapers() {
      return this.papersByTask
    },
    totalScore() {
      return this.flatQuestions.reduce((sum, q) => sum + (Number(q.score) || 0), 0)
    },
    canConfirmNote() {
      if (this.noteSaving) return false
      if (this.currentNoteQid == null || this.currentNoteQid === '') return false
      return (this.noteDraft || '').trim() !== String(this.confirmedDraft || '').trim()
    },
    noteStatusText() {
      if (this.currentNoteQid == null || this.currentNoteQid === '') return ''
      const draft = (this.noteDraft || '').trim()
      const changed = draft !== String(this.confirmedDraft || '').trim()
      if (changed) return '未确认'
      if (draft) return '已确认'
      return '未批注'
    },
    noteStatusSaved() {
      return this.noteStatusText === '已确认'
    },
    canConfirmPaperNote() {
      if (this.noteSaving || !this.activePaper || this.paperReviewEditingId != null) return false
      return !!(this.paperNoteDraft || '').trim()
    },
    paperReviewStatusText() {
      if (!this.activePaper) return ''
      const n = (this.paperReviews || []).length
      if (!n) return '未点评'
      return '共 ' + n + ' 条'
    },
    abilityVersionLabel() {
      return this.abilityVersionTab === 'final' ? '终版' : '过程版本'
    },
    activeAbilityKey() {
      if (this.activeAbilityTaskId == null || !this.activeAbilityName) return ''
      return this.abilityKey(this.activeAbilityTaskId, this.activeAbilityName)
    },
    abilityStats() {
      const s = (this.abilityDetail && this.abilityDetail.stats) || {}
      const groups = this.abilityGroups || []
      const questions = this.abilityFlatQuestions || []
      const authors = {}
      groups.forEach(g => {
        if (g && g.creatorUserName) authors[String(g.creatorUserName)] = true
        else if (g && g.creatorUserId != null) authors[String(g.creatorUserId)] = true
      })
      return {
        questionCount: s.questionCount != null ? s.questionCount : questions.length,
        paperCount: s.paperCount != null ? s.paperCount : groups.length,
        spaceCount: s.spaceCount != null ? s.spaceCount : 0,
        authorCount: s.authorCount != null ? s.authorCount : Object.keys(authors).length
      }
    },
    lineageVersions() {
      const list = this.lineageData && this.lineageData.versions
      return Array.isArray(list) ? list : []
    },
    lineageQuestions() {
      const list = this.lineageData && this.lineageData.questions
      return Array.isArray(list) ? list : []
    },
    lineageStats() {
      const s = (this.lineageData && this.lineageData.stats) || {}
      const questions = this.lineageQuestions
      const countBy = status => questions.filter(q => q && q.status === status).length
      const selectedVer = this.lineageVersions.find(v => v && v.selected)
      const currentFallback = selectedVer && selectedVer.questionCount != null
        ? Number(selectedVer.questionCount)
        : questions.filter(q => q && q.status !== 'removed').length
      return {
        versionCount: s.versionCount != null ? s.versionCount : this.lineageVersions.length,
        currentCount: s.currentCount != null ? s.currentCount : currentFallback,
        mergedCount: s.mergedCount != null ? s.mergedCount : questions.length,
        addedCount: s.addedCount != null ? s.addedCount : countBy('added'),
        removedCount: s.removedCount != null ? s.removedCount : countBy('removed'),
        keptCount: s.keptCount != null ? s.keptCount : countBy('kept')
      }
    }
  },
  watch: {
    groupedPapers: {
      immediate: true,
      handler(groups) {
        this.syncPaperAccordion(groups)
      }
    },
    abilityTasks: {
      immediate: true,
      handler(tasks) {
        this.syncAbilityAccordion(tasks)
      }
    }
  },
  created() {
    this.initPage()
  },
  beforeDestroy() {
    if (this.keywordTimer) clearTimeout(this.keywordTimer)
  },
  methods: {
    renderNoteHtml,
    isSameId(a, b) {
      if (a === null || a === undefined || b === null || b === undefined || a === '' || b === '') return false
      return String(a) === String(b)
    },
    questionDomId(qid) {
      return 'q-' + qid
    },
    noteKey(qid) {
      return String(qid)
    },
    formatTime(value) {
      if (!value) return '—'
      return String(value)
    },
    formatTimeToMinute(value) {
      if (value == null || value === '') return '—'
      const raw = String(value).trim()
      const normalized = raw.replace(/-/g, '/').replace('T', ' ').replace(/\.\d+/, '')
      const date = new Date(normalized)
      if (!isNaN(date.getTime())) {
        const pad = n => (n < 10 ? '0' + n : String(n))
        return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes())
      }
      const matched = raw.match(/^(\d{4}[-/]\d{1,2}[-/]\d{1,2})[ T](\d{1,2}:\d{2})/)
      if (matched) return matched[1].replace(/\//g, '-') + ' ' + matched[2]
      return raw.replace(/(\d{1,2}:\d{2}):\d{2}(?:\.\d+)?$/, '$1')
    },
    formatNoteTime(time) {
      if (time == null || time === '') return '—'
      const raw = typeof time === 'number' ? time : String(time).replace(/-/g, '/').replace('T', ' ')
      const d = new Date(raw)
      if (isNaN(d.getTime())) return String(time)
      const pad = n => (n < 10 ? '0' + n : String(n))
      return d.getFullYear() + '/' + pad(d.getMonth() + 1) + '/' + pad(d.getDate()) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes())
    },
    formatDifficulty(val) {
      if (val == null || val === '') return '—'
      return Number(val).toFixed(2)
    },
    markStatusLabel(status) {
      if (Number(status) === 2) return '已标记'
      if (Number(status) === 1) return '标记中'
      if (Number(status) === 0) return '未标记'
      return ''
    },
    isDimCollapsed(qid) {
      return this.dimCollapsed[qid] !== false
    },
    toggleDim(qid) {
      this.$set(this.dimCollapsed, qid, !this.isDimCollapsed(qid))
    },
    isMyNote(note) {
      if (!note) return false
      if (this.currentUserId != null && note.userId != null) {
        return this.isSameId(note.userId, this.currentUserId)
      }
      return note.author === this.currentAuthor
    },
    normalizeNotesList(raw) {
      if (raw == null || raw === '') return []
      if (Array.isArray(raw)) {
        return raw.filter(n => n && String(n.content || '').trim())
      }
      if (typeof raw === 'string' && raw.trim()) {
        return [{ content: raw, author: '', time: null }]
      }
      if (raw.content && String(raw.content).trim()) return [raw]
      return []
    },
    mapApiNotes(notes) {
      let list = notes
      if (typeof list === 'string') {
        const text = list.trim()
        if (!text) return []
        try {
          list = JSON.parse(text)
        } catch (e) {
          return [{ content: text, author: '', time: null, versionName: '' }]
        }
      }
      if (!Array.isArray(list)) {
        if (list && typeof list === 'object') list = [list]
        else return []
      }
      return list.map(n => {
        if (n == null) return null
        if (typeof n === 'string') {
          const content = n.trim()
          return content ? { content, author: '', time: null, versionName: '' } : null
        }
        const content = n.content || n.note || n.text || n.remark || ''
        return {
          id: n.id,
          questionId: n.questionId,
          userId: n.userId,
          author: n.userName || n.author || n.nickName || n.creatorName || '',
          content,
          time: n.createTime || n.time || n.createdAt || n.noteTime || null,
          versionName: n.versionName || n.versionLabel || n.version || ''
        }
      }).filter(n => n && String(n.content || '').trim())
    },
    getNotesForQuestion(qid) {
      const list = this.normalizeNotesList(this.notesMap[this.noteKey(qid)])
      return list.slice().sort((a, b) => {
        const ta = a.time != null ? new Date(String(a.time).replace(/-/g, '/')).getTime() : Infinity
        const tb = b.time != null ? new Date(String(b.time).replace(/-/g, '/')).getTime() : Infinity
        if (!isNaN(ta) && !isNaN(tb) && ta !== tb) return ta - tb
        return String(a.author || '').localeCompare(String(b.author || ''), 'zh-CN')
      })
    },
    notesOf(qid) {
      return this.getNotesForQuestion(qid)
    },
    hasAnyNote(qid) {
      return this.getNotesForQuestion(qid).length > 0
    },
    getMyNote(qid) {
      return this.getNotesForQuestion(qid).find(n => this.isMyNote(n)) || null
    },
    getMyNoteContent(qid) {
      const mine = this.getMyNote(qid)
      return mine ? String(mine.content || '') : ''
    },
    upsertLocalNote(qid, entry) {
      const key = this.noteKey(qid)
      const notes = { ...this.notesMap }
      const list = this.normalizeNotesList(notes[key]).slice()
      const idx = list.findIndex(n => {
        if (entry.id != null && n.id != null) return this.isSameId(n.id, entry.id)
        return this.isMyNote(n)
      })
      if (idx >= 0) list[idx] = Object.assign({}, list[idx], entry)
      else list.push(entry)
      notes[key] = list
      this.notesMap = notes
    },
    removeLocalNote(qid, noteId) {
      const key = this.noteKey(qid)
      const notes = { ...this.notesMap }
      let list = this.normalizeNotesList(notes[key]).slice()
      if (noteId != null) {
        list = list.filter(n => !this.isSameId(n.id, noteId))
      } else {
        list = list.filter(n => !this.isMyNote(n))
      }
      if (list.length) notes[key] = list
      else delete notes[key]
      this.notesMap = notes
    },
    formatNowTime() {
      const d = new Date()
      const pad = n => (n < 10 ? '0' + n : String(n))
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) +
        ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds())
    },
    async createNote(qid, content) {
      const res = await this.$Api.Paired.createExamPaperQuestionNote({
        questionId: qid,
        content
      })
      if (!(res && res.code == 200)) {
        throw new Error((res && res.msg) || '新增批注失败')
      }
      this.upsertLocalNote(qid, {
        id: res.data,
        questionId: qid,
        userId: this.currentUserId,
        author: this.currentAuthor,
        content,
        time: this.formatNowTime()
      })
      return res.data
    },
    async updateNote(noteId, qid, content) {
      const res = await this.$Api.Paired.updateExamPaperQuestionNote({
        id: noteId,
        content
      })
      if (!(res && res.code == 200)) {
        throw new Error((res && res.msg) || '修改批注失败')
      }
      this.upsertLocalNote(qid, {
        id: noteId,
        questionId: qid,
        userId: this.currentUserId,
        author: this.currentAuthor,
        content,
        time: this.formatNowTime()
      })
      return true
    },
    async deleteNote(noteId, qid) {
      const res = await this.$Api.Paired.deleteExamPaperQuestionNote({ id: noteId })
      if (!(res && res.code == 200)) {
        throw new Error((res && res.msg) || '删除批注失败')
      }
      this.removeLocalNote(qid, noteId)
      return true
    },
    async persistNote(qid, rawText) {
      if (qid == null || qid === '') return false
      const val = String(rawText || '').trim()
      const mine = this.getMyNote(qid)

      if (val) {
        if (mine && mine.id != null) {
          await this.updateNote(mine.id, qid, val)
        } else {
          await this.createNote(qid, val)
        }
        return true
      }

      if (mine && mine.id != null) {
        await this.deleteNote(mine.id, qid)
      } else if (mine) {
        this.removeLocalNote(qid, mine.id)
      }
      return false
    },
    async confirmCurrentNote() {
      if (this.currentNoteQid == null || this.currentNoteQid === '') return
      if (this.noteSaving) return
      const draft = (this.noteDraft || '').trim()
      if (!draft && !String(this.confirmedDraft || '').trim()) {
        this.$message.warning('请先填写批注内容')
        return
      }
      this.noteSaving = true
      try {
        const saved = await this.persistNote(this.currentNoteQid, this.noteDraft)
        this.confirmedDraft = saved ? draft : ''
        this.noteDraft = this.confirmedDraft
        this.noteDirty = false
        this.$message.success('批注已确认')
      } catch (err) {
        console.log('保存批注失败', err)
        this.$message.error((err && err.message) || '保存批注失败')
      } finally {
        this.noteSaving = false
      }
    },
    clearCurrentNote() {
      if (this.currentNoteQid == null || this.currentNoteQid === '') return
      const hasSaved = !!String(this.getMyNoteContent(this.currentNoteQid) || '').trim()
      const hasDraft = !!(this.noteDraft || '').trim()
      if (!hasSaved && !hasDraft) return
      this.$confirm('确定清除你在第 ' + this.currentNoteNo + ' 题的批注吗？其他人的批注将保留。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        if (this.noteSaving) return
        this.noteSaving = true
        try {
          this.noteDraft = ''
          await this.persistNote(this.currentNoteQid, '')
          this.confirmedDraft = ''
          this.noteDirty = false
          this.$message.success('已清除本人批注')
        } catch (err) {
          console.log('清除批注失败', err)
          this.$message.error((err && err.message) || '清除批注失败')
          this.noteDraft = this.getMyNoteContent(this.currentNoteQid)
          this.confirmedDraft = this.noteDraft
        } finally {
          this.noteSaving = false
        }
      }).catch(() => {})
    },
    resolveActivePaperId(paper) {
      const p = paper || this.activePaper
      if (!p) return null
      if (p.paperId != null && p.paperId !== '') return p.paperId
      return null
    },
    mapPaperReviewItem(raw) {
      const n = raw || {}
      return {
        id: n.id,
        paperId: n.paperId,
        userId: n.userId,
        author: n.userName || n.author || n.nickName || '',
        content: String(n.content || '').trim(),
        time: n.createTime || n.time || n.createdAt || null
      }
    },
    resetPaperReviewState() {
      this.paperReviews = []
      this.paperReviewLoading = false
      this.paperNoteDraft = ''
      this.paperNoteDirty = false
      this.paperReviewEditingId = null
      this.paperReviewEditDraft = ''
    },
    async fetchPaperReviews(paper) {
      const paperId = this.resolveActivePaperId(paper)
      if (paperId == null || paperId === '') {
        this.paperReviews = []
        return
      }
      this.paperReviewLoading = true
      try {
        const res = await this.$Api.Paired.getExamPaperReviewList({ paperId })
        if (!(res && res.code == 200)) {
          throw new Error((res && res.msg) || '获取整卷点评失败')
        }
        const list = Array.isArray(res.data) ? res.data : []
        this.paperReviews = list.map(item => this.mapPaperReviewItem(item)).filter(item => item.content)
      } catch (err) {
        console.log('[examPaper/review/list] 请求失败', err)
        this.paperReviews = []
        this.$message.error((err && err.message) || '获取整卷点评失败')
      } finally {
        this.paperReviewLoading = false
      }
    },
    switchNoteScope(scope) {
      const next = scope === 'paper' ? 'paper' : 'question'
      if (this.noteScope === next) return
      if (this.paperReviewEditingId != null) {
        this.cancelEditPaperReview()
      }
      this.noteScope = next
      if (next === 'paper' && this.activePaper && !this.paperReviewLoading) {
        this.fetchPaperReviews(this.activePaper)
      }
    },
    onPaperNoteInput() {
      this.paperNoteDirty = !!(this.paperNoteDraft || '').trim()
    },
    clearPaperNoteDraft() {
      this.paperNoteDraft = ''
      this.paperNoteDirty = false
    },
    async confirmPaperNote() {
      if (!this.activePaper || this.noteSaving || this.paperReviewEditingId != null) return
      const draft = (this.paperNoteDraft || '').trim()
      if (!draft) {
        this.$message.warning('请先填写整卷点评内容')
        return
      }
      const paperId = this.resolveActivePaperId(this.activePaper)
      if (paperId == null || paperId === '') {
        this.$message.warning('缺少试卷ID，暂时无法提交点评')
        return
      }
      this.noteSaving = true
      try {
        const res = await this.$Api.Paired.createExamPaperReview({
          paperId,
          content: draft
        })
        if (!(res && res.code == 200)) {
          throw new Error((res && res.msg) || '新增整卷点评失败')
        }
        const newId = res.data
        this.paperReviews = this.paperReviews.concat([{
          id: newId,
          paperId,
          userId: this.currentUserId,
          author: this.currentAuthor,
          content: draft,
          time: this.formatNowTime()
        }])
        this.clearPaperNoteDraft()
        this.$message.success('整卷点评已提交')
      } catch (err) {
        console.log('保存整卷点评失败', err)
        this.$message.error((err && err.message) || '保存整卷点评失败')
      } finally {
        this.noteSaving = false
      }
    },
    startEditPaperReview(item) {
      if (!item || item.id == null || this.noteSaving) return
      if (!this.isMyNote(item)) {
        this.$message.warning('只能修改自己的点评')
        return
      }
      this.paperReviewEditingId = item.id
      this.paperReviewEditDraft = String(item.content || '')
    },
    cancelEditPaperReview() {
      this.paperReviewEditingId = null
      this.paperReviewEditDraft = ''
    },
    async saveEditPaperReview() {
      if (this.paperReviewEditingId == null || this.noteSaving) return
      const draft = (this.paperReviewEditDraft || '').trim()
      if (!draft) {
        this.$message.warning('点评内容不能为空')
        return
      }
      const id = this.paperReviewEditingId
      this.noteSaving = true
      try {
        const res = await this.$Api.Paired.updateExamPaperQuestionNote({
          id,
          content: draft
        })
        if (!(res && res.code == 200)) {
          throw new Error((res && res.msg) || '修改整卷点评失败')
        }
        this.paperReviews = this.paperReviews.map(item => {
          if (!this.isSameId(item.id, id)) return item
          return Object.assign({}, item, { content: draft })
        })
        this.cancelEditPaperReview()
        this.$message.success('整卷点评已修改')
      } catch (err) {
        console.log('修改整卷点评失败', err)
        this.$message.error((err && err.message) || '修改整卷点评失败')
      } finally {
        this.noteSaving = false
      }
    },
    deletePaperReview(item) {
      if (!item || item.id == null) return
      if (!this.isMyNote(item)) {
        this.$message.warning('只能删除自己的点评')
        return
      }
      this.$confirm('确定删除这条整卷点评吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        if (this.noteSaving) return
        this.noteSaving = true
        try {
          const res = await this.$Api.Paired.deleteExamPaperQuestionNote({ id: item.id })
          if (!(res && res.code == 200)) {
            throw new Error((res && res.msg) || '删除整卷点评失败')
          }
          this.paperReviews = this.paperReviews.filter(n => !this.isSameId(n.id, item.id))
          if (this.isSameId(this.paperReviewEditingId, item.id)) this.cancelEditPaperReview()
          this.$message.success('已删除整卷点评')
        } catch (err) {
          console.log('删除整卷点评失败', err)
          this.$message.error((err && err.message) || '删除整卷点评失败')
        } finally {
          this.noteSaving = false
        }
      }).catch(() => {})
    },
    mapExamQuestionItem(raw, index, specMeta) {
      const item = raw || {}
      const meta = specMeta || {}
      const specMode = normalizeSpecMode(meta.specMode != null ? meta.specMode : item.specMode)
      const specColumns = Array.isArray(meta.specColumns)
        ? meta.specColumns
        : (Array.isArray(item.specColumns) ? item.specColumns : [])
      const mapped = {
        id: item.id,
        paperId: item.paperId,
        sectionId: item.sectionId,
        sort: item.sort,
        thirdQuestionId: item.thirdQuestionId,
        typeId: item.typeId,
        type: item.typeName || '题目',
        partName: item.partName || '',
        status: item.status,
        source: item.source || '',
        stem: item.stem || '',
        answer: item.answer || item.answerParsed || '',
        answerParsed: item.answerParsed || '',
        analysis: this.normalizeExplanation(item.explanation),
        difficulty: item.difficulty,
        difficultyLevelId: item.difficultyLevelId,
        difficultyLevelName: item.difficultyLevelName || '',
        knowledgeName: formatDimValue(item.knowledgeName),
        keyAbilityName: formatDimValue(item.keyAbilityName),
        subjectAbilityName: formatDimValue(item.subjectAbilityName),
        requirement: formatDimValue(item.requirement),
        objective: formatDimValue(item.objective),
        customMarks: normalizeCustomMarks(item.customMarks),
        specMode,
        markStatus: item.markStatus == null || item.markStatus === '' ? null : Number(item.markStatus),
        score: item.totalScore,
        noteCount: item.noteCount || 0,
        notes: this.mapApiNotes(item.notes),
        no: index + 1,
        raw: item
      }
      mapped.dimFields = this.buildDimFields(mapped, specMode, specColumns)
      return mapped
    },
    /**
     * 细目列统一规则（当前试卷 / 过程溯源 / 关键能力）：
     * 1) 有 specColumns → 用 columnName 原样、按接口顺序
     * 2) 否则用 customMarks 的键
     * 3) 都没有且 specMode=0 → 退回固定五项
     * 4) specMode=1 且无列名 → 空数组（不展示细目块）
     * 「题号」与隐藏名单列不展示
     */
    buildDimFields(q, specMode, specColumns) {
      const mode = normalizeSpecMode(specMode != null ? specMode : (q && q.specMode))
      const isVisibleName = name => {
        const text = String(name || '').trim()
        return Boolean(text) && text !== '题号' && !isHiddenDimLabel(text)
      }

      const cols = (Array.isArray(specColumns) ? specColumns : [])
        .filter(col => col && isVisibleName(col.columnName))
      if (cols.length) {
        return cols.map(col => {
          const name = String(col.columnName).trim()
          return {
            key: name,
            label: name,
            value: this.resolveSpecColumnValue(q, col)
          }
        })
      }

      const marks = (q && q.customMarks) || {}
      const markNames = Object.keys(marks).filter(isVisibleName)
      if (markNames.length) {
        return markNames.map(name => ({
          key: name,
          label: name,
          value: formatDimValue(marks[name])
        }))
      }

      if (mode === 0) {
        return FIXED_DIM_FIELDS.map(field => ({
          key: field.key,
          label: field.label,
          value: formatDimValue(q && q[field.field])
        }))
      }

      return []
    },
    resolveSpecColumnValue(q, col) {
      if (!q || !col) return ''
      const builtinKey = col.builtinKey
      const field = builtinKey ? BUILTIN_FIELD_BY_KEY[builtinKey] : ''
      if (field) {
        const fromBuiltin = formatDimValue(q[field])
        if (fromBuiltin) return fromBuiltin
      }
      const name = col.columnName != null ? String(col.columnName) : ''
      const marks = q.customMarks || {}
      if (name && marks[name] != null) return formatDimValue(marks[name])
      return ''
    },
    lineageDimFields(q) {
      if (q && Array.isArray(q.dimFields) && q.dimFields.length) {
        return q.dimFields.filter(dim => dim && dim.value)
      }
      return this.buildDimFields(q, q && q.specMode, []).filter(dim => dim && dim.value)
    },
    // 去掉解析里的「分析」分段；【详解】【点睛】等标题标蓝
    normalizeExplanation(html) {
      if (!html) return ''
      let out = String(html)
      out = out.replace(/<div\b[^>]*\bseg-name\s*=\s*["']分析["'][^>]*>[\s\S]*?<\/div>/gi, '')
      out = out.replace(/【分析】[\s\S]*?(?=【详解】|【点睛】|$)/g, '')
      out = out.replace(/【(详解|点睛|解析|答案)】/g, '<span class="q-extra-label">【$1】</span>')
      return out.trim()
    },
    groupByType(questions) {
      const map = {}
      const order = []
      ;(questions || []).forEach(q => {
        const type = q.type || '题目'
        if (!map[type]) {
          map[type] = []
          order.push(type)
        }
        map[type].push(q)
      })
      return order.map(name => ({
        name,
        questions: map[name]
      }))
    },
    buildNotesMapFromQuestions(questions) {
      const map = {}
      ;(questions || []).forEach(q => {
        map[this.noteKey(q.id)] = (q.notes || []).slice()
      })
      return map
    },
    async initPage() {
      this.listLoading = true
      try {
        await this.fetchExpertPaperList(true)
      } finally {
        this.listLoading = false
      }
    },
    flattenExpertPapers(list) {
      const rows = Array.isArray(list) ? list : []
      const grouped = rows.some(item => item && Array.isArray(item.papers))
      if (!grouped) return rows.map(item => this.mapExamPaperItem(item))
      const papers = []
      rows.forEach(task => {
        const taskPapers = Array.isArray(task.papers) ? task.papers : []
        taskPapers.forEach(paper => {
          papers.push(this.mapExamPaperItem(Object.assign({}, paper, {
            taskId: paper.taskId != null && paper.taskId !== '' ? paper.taskId : task.id,
            taskName: paper.taskName || task.name || '',
            taskGoal: paper.taskGoal || task.goal || null
          })))
        })
      })
      return papers
    },
    mapExamPaperItem(raw) {
      const item = raw || {}
      return {
        id: item.relId,
        relId: item.relId,
        paperId: item.paperId,
        spaceId: item.spxId,
        spxName: item.spxName || '',
        title: item.mainTitle || item.subTitle || '未命名试卷',
        subTitle: item.subTitle || '',
        coverLabel: item.subTitle || '',
        grade: '',
        questionCount: 0,
        submittedAt: item.createTime || item.updateTime || '',
        creator: item.creatorUserName || item.creatorName || '',
        author: item.submitUserName || item.authorName || '',
        isFinal: item.isFinal == null || item.isFinal === '' ? null : Number(item.isFinal),
        taskId: item.taskId != null && item.taskId !== '' ? item.taskId : null,
        taskName: item.taskName || '',
        taskGoal: item.taskGoal || item.goal || null,
        reviewStatus: item.reviewStatus == null || item.reviewStatus === '' ? null : Number(item.reviewStatus),
        reviewReason: item.reviewReason || '',
        reviewUserName: item.reviewUserName || '',
        reviewTime: item.reviewTime || '',
        answerSheetIdentifier: item.answerSheetIdentifier,
        raw
      }
    },
    buildSpacesFromPapers(papers) {
      const map = {}
      ;(papers || []).forEach(item => {
        if (item.spaceId == null || item.spaceId === '') return
        const key = String(item.spaceId)
        if (!map[key]) {
          map[key] = {
            id: item.spaceId,
            name: item.spxName || ('空间' + item.spaceId)
          }
        }
      })
      return Object.keys(map).map(k => map[k])
    },
    async fetchExpertPaperList(autoSelect = false) {
      const projectId = this.currentProject && this.currentProject.id
      if (projectId == null || projectId === '') {
        this.spaces = []
        this.allPapers = []
        this.spaces = []
        this.allPapers = []
        this.clearActivePaper()
        this.$message.warning('缺少项目ID，暂时无法获取试卷')
        return
      }
      try {
        const res = await this.$Api.Paired.getExamPaperListForExpert({ projectId })
        if (!(res && res.code == 200)) {
          this.spaces = []
          this.allPapers = []
          this.clearActivePaper()
          this.$message.error((res && res.msg) || '获取专家试卷列表失败')
          return
        }
        const list = Array.isArray(res.data) ? res.data : []
        this.allPapers = this.flattenExpertPapers(list)
        this.spaces = this.buildSpacesFromPapers(this.allPapers)
        const visible = this.groupedPapers.reduce((acc, g) => acc.concat(g.papers || []), [])
        const stillVisible = visible.some(item => this.isSameId(item.id, this.activePaperId))
        if (!stillVisible) {
          if (visible.length) this.selectPaper(visible[0])
          else this.clearActivePaper()
        } else if (autoSelect && visible.length && !this.activePaper) {
          this.selectPaper(visible[0])
        }
      } catch (err) {
        console.log('获取专家试卷列表失败', err)
        this.spaces = []
        this.allPapers = []
        this.clearActivePaper()
        this.$message.error('获取专家试卷列表失败')
      }
    },
    clearActivePaper() {
      this.activePaperId = null
      this.activePaper = null
      this.flatQuestions = []
      this.paperSections = []
      this.noteScope = 'question'
      this.resetPaperReviewState()
      this.resetLineageState()
      this.activeDetailTab = 'current'
    },
    resetLineageState() {
      this.lineageData = null
      this.lineageError = ''
      this.lineagePaperKey = null
      this.lineageLoading = false
    },
    onFilterChange() {
      const list = this.groupedPapers.reduce((acc, g) => acc.concat(g.papers || []), [])
      const stillVisible = list.some(item => this.isSameId(item.id, this.activePaperId))
      if (!stillVisible) {
        if (list.length) this.selectPaper(list[0])
        else this.clearActivePaper()
      }
    },
    onKeywordInput() {
      if (this.keywordTimer) clearTimeout(this.keywordTimer)
      this.keywordTimer = setTimeout(() => {
        if (this.viewMode === 'ability') this.fetchAbilityCatalog(true)
        else this.onFilterChange()
      }, 200)
    },
    abilityKey(taskId, abilityName) {
      return String(taskId) + '||' + String(abilityName || '')
    },
    syncPaperAccordion(groups) {
      const next = {}
      ;(groups || []).forEach((group, index) => {
        next[group.id] = index !== 0
      })
      this.collapsedTasks = next
    },
    syncAbilityAccordion(tasks) {
      const next = {}
      ;(tasks || []).forEach((task, index) => {
        next[task.taskId] = index !== 0
      })
      this.collapsedAbilityTasks = next
    },
    toggleAbilityTask(taskId) {
      const opening = this.collapsedAbilityTasks[taskId] !== false
      const next = {}
      this.abilityTasks.forEach(task => {
        next[task.taskId] = this.isSameId(task.taskId, taskId) ? !opening : true
      })
      this.collapsedAbilityTasks = next
      if (!opening) return
      const task = this.abilityTasks.find(item => this.isSameId(item.taskId, taskId))
      const first = task && task.abilities && task.abilities[0]
      if (task && first) this.selectAbility(task, first)
    },
    switchViewMode(mode) {
      const next = mode === 'ability' ? 'ability' : 'paper'
      if (next === this.viewMode) return
      if (this.noteDirty) {
        this.$confirm(
          '第 ' + this.currentNoteNo + ' 题批注尚未确认。<br>确定：先确认并切换<br>取消：放弃修改并切换',
          '提示',
          {
            confirmButtonText: '确认并切换',
            cancelButtonText: '放弃修改',
            type: 'warning',
            dangerouslyUseHTMLString: true,
            distinguishCancelAndClose: true
          }
        ).then(async () => {
          this.noteSaving = true
          try {
            const saved = await this.persistNote(this.currentNoteQid, this.noteDraft)
            this.confirmedDraft = saved ? String(this.noteDraft || '').trim() : ''
            this.noteDraft = this.confirmedDraft
            this.noteDirty = false
            this.doSwitchViewMode(next)
          } catch (err) {
            this.$message.error((err && err.message) || '保存批注失败')
          } finally {
            this.noteSaving = false
          }
        }).catch(action => {
          if (action === 'cancel') {
            this.noteDirty = false
            this.noteDraft = this.confirmedDraft
            this.doSwitchViewMode(next)
          }
        })
        return
      }
      this.doSwitchViewMode(next)
    },
    doSwitchViewMode(next) {
      this.viewMode = next
      this.showAnswer = false
      this.currentNoteQid = ''
      this.currentNoteNo = 0
      this.noteDraft = ''
      this.confirmedDraft = ''
      this.noteDirty = false
      if (next === 'ability') {
        this.activePaperId = null
        this.activePaper = null
        this.activeDetailTab = 'current'
        this.resetLineageState()
        this.fetchAbilityCatalog(true)
      } else {
        this.clearAbilitySelection()
        if (!this.activePaper) {
          const visible = this.groupedPapers.reduce((acc, g) => acc.concat(g.papers || []), [])
          if (visible.length) this.selectPaper(visible[0])
        }
      }
    },
    clearAbilitySelection() {
      this.activeAbilityTaskId = null
      this.activeAbilityName = ''
      this.abilityDetail = {}
      this.abilityGroups = []
      this.abilityFlatQuestions = []
      this.notesMap = {}
    },
    switchAbilityVersionTab(ver) {
      const next = ver === 'final' ? 'final' : 'process'
      if (next === this.abilityVersionTab) return
      const go = () => this.applyAbilityVersionTab(next)
      if (this.noteDirty) {
        this.$confirm(
          '第 ' + this.currentNoteNo + ' 题批注尚未确认。<br>确定：先确认并切换<br>取消：放弃修改并切换',
          '提示',
          {
            confirmButtonText: '确认并切换',
            cancelButtonText: '放弃修改',
            type: 'warning',
            dangerouslyUseHTMLString: true,
            distinguishCancelAndClose: true
          }
        ).then(async () => {
          this.noteSaving = true
          try {
            const saved = await this.persistNote(this.currentNoteQid, this.noteDraft)
            this.confirmedDraft = saved ? String(this.noteDraft || '').trim() : ''
            this.noteDraft = this.confirmedDraft
            this.noteDirty = false
            go()
          } catch (err) {
            this.$message.error((err && err.message) || '保存批注失败')
          } finally {
            this.noteSaving = false
          }
        }).catch(action => {
          if (action === 'cancel') {
            this.noteDirty = false
            this.noteDraft = this.confirmedDraft
            go()
          }
        })
        return
      }
      go()
    },
    async applyAbilityVersionTab(next) {
      // 左侧目录始终按过程版本，切换终版不重拉目录
      if (next === 'process') {
        this.abilityVersionTab = 'process'
        if (this.activeAbilityTaskId != null && this.activeAbilityName) {
          this.abilityDetailLoading = true
          try {
            await this.fetchAbilityQuestions(this.activeAbilityTaskId, this.activeAbilityName, 'process')
          } finally {
            this.abilityDetailLoading = false
          }
        }
        return
      }
      if (this.activeAbilityTaskId == null || !this.activeAbilityName) {
        this.abilityVersionTab = 'final'
        return
      }
      this.abilityDetailLoading = true
      try {
        const result = await this.fetchAbilityQuestions(
          this.activeAbilityTaskId,
          this.activeAbilityName,
          'final',
          { silentEmpty: true }
        )
        if (result !== 'ok') {
          if (result === 'empty') this.$message.warning('当前任务没有终版')
          await this.fetchAbilityQuestions(this.activeAbilityTaskId, this.activeAbilityName, 'process')
          return
        }
        this.abilityVersionTab = 'final'
      } finally {
        this.abilityDetailLoading = false
      }
    },
    async fetchAbilityCatalog(autoSelect) {
      const projectId = this.currentProject && this.currentProject.id
      if (projectId == null || projectId === '') {
        this.abilityTasks = []
        this.clearAbilitySelection()
        this.$message.warning('缺少项目ID，暂时无法获取关键能力目录')
        return
      }
      this.abilityCatalogLoading = true
      try {
        // 左侧任务列表始终按过程版本加载
        const params = {
          projectId,
          versionType: 'process'
        }
        if (this.keyword) params.keyword = this.keyword
        const res = await this.$Api.Paired.getExamPaperAbilityCatalogForExpert(params)
        if (!(res && res.code == 200)) {
          this.abilityTasks = []
          this.clearAbilitySelection()
          this.$message.error((res && res.msg) || '获取关键能力目录失败')
          return
        }
        const data = res.data || {}
        const tasks = Array.isArray(data.tasks) ? data.tasks : []
        this.abilityTasks = tasks.map(t => ({
          taskId: t.taskId,
          taskName: t.taskName || '',
          abilities: (Array.isArray(t.abilities) ? t.abilities : []).map(a => ({
            abilityName: a.abilityName || '',
            questionCount: a.questionCount,
            paperCount: a.paperCount,
            spaceCount: a.spaceCount,
            authorCount: a.authorCount
          })).filter(a => a.abilityName)
        })).filter(t => t.taskId != null && (t.abilities || []).length)

        const still =
          this.activeAbilityTaskId != null &&
          this.activeAbilityName &&
          this.abilityTasks.some(t =>
            this.isSameId(t.taskId, this.activeAbilityTaskId) &&
            (t.abilities || []).some(a => a.abilityName === this.activeAbilityName)
          )
        if (still) {
          const task = this.abilityTasks.find(t => this.isSameId(t.taskId, this.activeAbilityTaskId))
          const ab = (task.abilities || []).find(a => a.abilityName === this.activeAbilityName)
          if (task && ab) await this.selectAbility(task, ab)
        } else if (autoSelect && this.abilityTasks.length && this.abilityTasks[0].abilities.length) {
          await this.selectAbility(this.abilityTasks[0], this.abilityTasks[0].abilities[0])
        } else {
          this.clearAbilitySelection()
        }
      } catch (err) {
        console.log('[examPaper/abilityCatalogForExpert] 请求失败', err)
        this.abilityTasks = []
        this.clearAbilitySelection()
        this.$message.error('获取关键能力目录失败')
      } finally {
        this.abilityCatalogLoading = false
      }
    },
    async selectAbility(task, ability) {
      if (!task || !ability || !ability.abilityName) return
      const same =
        this.isSameId(task.taskId, this.activeAbilityTaskId) &&
        this.activeAbilityName === ability.abilityName &&
        this.abilityGroups.length
      if (same) return

      this.activeAbilityTaskId = task.taskId
      this.activeAbilityName = ability.abilityName
      this.showAnswer = false
      this.currentNoteQid = ''
      this.currentNoteNo = 0
      this.noteDraft = ''
      this.confirmedDraft = ''
      this.noteDirty = false
      this.abilityDetailLoading = true
      try {
        if (this.abilityVersionTab === 'final') {
          const result = await this.fetchAbilityQuestions(task.taskId, ability.abilityName, 'final', {
            silentEmpty: true
          })
          if (result !== 'ok') {
            this.abilityVersionTab = 'process'
            if (result === 'empty') this.$message.warning('该任务没有终版，已回到过程版本')
            await this.fetchAbilityQuestions(task.taskId, ability.abilityName, 'process')
          }
        } else {
          await this.fetchAbilityQuestions(task.taskId, ability.abilityName, 'process')
        }
      } finally {
        this.abilityDetailLoading = false
      }
    },
    // 返回 'ok' | 'empty' | 'error'
    async fetchAbilityQuestions(taskId, abilityName, versionType, options) {
      const projectId = this.currentProject && this.currentProject.id
      if (projectId == null || projectId === '') {
        this.$message.warning('缺少项目ID')
        return 'error'
      }
      const vt = versionType === 'final' ? 'final' : 'process'
      const silentEmpty = options && options.silentEmpty
      try {
        const res = await this.$Api.Paired.getExamPaperAbilityQuestionsForExpert({
          projectId,
          taskId,
          abilityName,
          versionType: vt
        })
        if (!(res && res.code == 200)) {
          this.abilityDetail = {}
          this.abilityGroups = []
          this.abilityFlatQuestions = []
          this.notesMap = {}
          if (!silentEmpty) this.$message.error((res && res.msg) || '获取关键能力题目失败')
          return 'error'
        }
        const data = res.data || {}
        const groups = Array.isArray(data.groups) ? data.groups : []
        const allRawQuestions = []
        groups.forEach(g => {
          ;(Array.isArray(g.questions) ? g.questions : []).forEach(q => allRawQuestions.push(q))
        })
        // 任务级 specMode / specColumns（非固定表头列定义）；无表头时并集兜底
        const abilitySpecMeta = this.dimSpecMetaFromPayload(data, allRawQuestions)
        this.abilitySpecMode = abilitySpecMeta.specMode
        this.abilitySpecColumns = abilitySpecMeta.specColumns
        let flatNo = 0
        const flat = []
        const notes = {}
        this.abilityGroups = groups.map(g => {
          const questions = (Array.isArray(g.questions) ? g.questions : []).map(raw => {
            // 批注/细目挂在 questionId 上
            const mapped = this.mapExamQuestionItem(Object.assign({}, raw, {
              id: raw.questionId != null ? raw.questionId : raw.id,
              questionId: raw.questionId != null ? raw.questionId : raw.id,
              totalScore: raw.totalScore != null ? raw.totalScore : raw.score,
              explanation: raw.explanation || raw.analysis
            }), flatNo, abilitySpecMeta)
            flatNo += 1
            mapped.no = raw.displayNo != null ? raw.displayNo : mapped.no
            flat.push(mapped)
            notes[this.noteKey(mapped.id)] = (mapped.notes || []).slice()
            return mapped
          })
          return {
            paperId: g.paperId,
            relId: g.relId,
            spxId: g.spxId,
            spxName: g.spxName || '',
            creatorUserId: g.creatorUserId,
            creatorUserName: g.creatorUserName || '',
            mainTitle: g.mainTitle || '',
            isFinal: g.isFinal,
            sendTime: g.sendTime || '',
            questions
          }
        })
        const stats = data.stats || {}
        this.abilityDetail = {
          taskId: data.taskId != null ? data.taskId : taskId,
          taskName: data.taskName || '',
          abilityName: data.abilityName || abilityName,
          versionType: data.versionType || vt,
          specMode: abilitySpecMeta.specMode,
          specColumns: abilitySpecMeta.specColumns,
          stats: {
            questionCount: stats.questionCount,
            paperCount: stats.paperCount,
            spaceCount: stats.spaceCount,
            authorCount: stats.authorCount
          }
        }
        this.abilityFlatQuestions = flat
        this.notesMap = notes
        if (!flat.length) return 'empty'
        this.$nextTick(() => {
          this.selectQuestion(flat[0].id, false, true)
        })
        return 'ok'
      } catch (err) {
        console.log('[examPaper/abilityQuestionsForExpert] 请求失败', err)
        this.abilityDetail = {}
        this.abilityGroups = []
        this.abilityFlatQuestions = []
        this.notesMap = {}
        if (!silentEmpty) this.$message.error('获取关键能力题目失败')
        return 'error'
      }
    },
    async selectPaper(item) {
      if (!item || item.id == null || item.id === '') return
      if (this.isSameId(item.id, this.activePaperId) && this.activePaper) return
      this.activePaperId = item.id
      this.detailLoading = true
      this.showAnswer = false
      this.currentNoteQid = ''
      this.currentNoteNo = 0
      this.noteDraft = ''
      this.confirmedDraft = ''
      this.noteDirty = false
      this.noteScope = 'question'
      this.resetPaperReviewState()
      this.flatQuestions = []
      this.paperSections = []
      this.paperSpecMode = 0
      this.paperSpecColumns = []
      this.notesMap = {}
      this.resetLineageState()
      try {
        this.activePaper = { ...item }
        this.fetchPaperReviews(this.activePaper)
        if (this.activeDetailTab === 'lineage') {
          await this.fetchLineageForExpert(item)
        } else {
          const questions = await this.fetchExamPaperQuestionList(item.paperId, item.taskId)
          this.flatQuestions = questions
          this.paperSections = this.groupByType(questions)
          this.notesMap = this.buildNotesMapFromQuestions(questions)
          this.$nextTick(() => {
            if (questions.length) this.selectQuestion(questions[0].id, false, true)
          })
        }
      } finally {
        this.detailLoading = false
      }
    },
    switchDetailTab(tab) {
      const next = tab === 'lineage' ? 'lineage' : 'current'
      if (next === this.activeDetailTab) return
      if (this.activeDetailTab === 'current' && this.noteDirty) {
        this.$confirm(
          '第 ' + this.currentNoteNo + ' 题批注尚未确认。<br>确定：先确认并切换<br>取消：放弃修改并切换',
          '提示',
          {
            confirmButtonText: '确认并切换',
            cancelButtonText: '放弃修改',
            type: 'warning',
            dangerouslyUseHTMLString: true,
            distinguishCancelAndClose: true
          }
        ).then(async () => {
          this.noteSaving = true
          try {
            const saved = await this.persistNote(this.currentNoteQid, this.noteDraft)
            this.confirmedDraft = saved ? String(this.noteDraft || '').trim() : ''
            this.noteDraft = this.confirmedDraft
            this.noteDirty = false
            this.doSwitchDetailTab(next)
          } catch (err) {
            console.log('保存批注失败', err)
            this.$message.error((err && err.message) || '保存批注失败')
          } finally {
            this.noteSaving = false
          }
        }).catch(action => {
          if (action === 'cancel') {
            this.noteDirty = false
            this.noteDraft = this.confirmedDraft
            this.doSwitchDetailTab(next)
          }
        })
        return
      }
      this.doSwitchDetailTab(next)
    },
    async doSwitchDetailTab(next) {
      this.activeDetailTab = next
      if (next === 'lineage') {
        await this.ensureLineageLoaded()
      } else if (this.activePaper && (!this.flatQuestions || !this.flatQuestions.length)) {
        this.detailLoading = true
        try {
          const item = this.activePaper
          const questions = await this.fetchExamPaperQuestionList(item.paperId, item.taskId)
          this.flatQuestions = questions
          this.paperSections = this.groupByType(questions)
          this.notesMap = this.buildNotesMapFromQuestions(questions)
          this.$nextTick(() => {
            if (questions.length) this.selectQuestion(questions[0].id, false, true)
          })
        } finally {
          this.detailLoading = false
        }
      }
    },
    paperLineageKey(item) {
      if (!item) return null
      if (item.paperId != null && item.paperId !== '') return 'p:' + item.paperId
      if (item.relId != null && item.relId !== '') return 'r:' + item.relId
      return null
    },
    async ensureLineageLoaded(force) {
      const item = this.activePaper
      if (!item) return
      const key = this.paperLineageKey(item)
      if (!force && this.lineageData && this.lineagePaperKey === key) return
      await this.fetchLineageForExpert(item)
    },
    lineageErrorMessage(res, err) {
      if (res) {
        if (typeof res === 'string') {
          const text = res.trim()
          if (/not\s*found|404/i.test(text) || text.startsWith('<!')) {
            return '过程溯源接口不存在或未发布（404）'
          }
          return text.slice(0, 120) || '获取过程溯源失败'
        }
        if (res.msg) return res.msg
        if (res.message) return res.message
        if (res.error) return String(res.error)
        if (res.code != null) return '获取过程溯源失败（code=' + res.code + '）'
      }
      if (err) {
        const data = err.response && err.response.data
        if (data) {
          if (typeof data === 'string') return this.lineageErrorMessage(data)
          if (data.msg || data.message || data.error) return this.lineageErrorMessage(data)
        }
        if (err.message) return err.message
      }
      return '获取过程溯源失败'
    },
    async fetchLineageForExpert(item) {
      const projectId = this.currentProject && this.currentProject.id
      if (projectId == null || projectId === '') {
        this.lineageData = null
        this.lineageError = '缺少项目ID'
        this.$message.warning('缺少项目ID，暂时无法溯源')
        return
      }
      if ((item.paperId == null || item.paperId === '') && (item.relId == null || item.relId === '')) {
        this.lineageData = null
        this.lineageError = '试卷ID和绑定ID不能同时为空'
        this.$message.warning('试卷ID和绑定ID不能同时为空')
        return
      }
      this.lineageLoading = true
      this.lineageError = ''
      try {
        const params = { projectId }
        if (item.paperId != null && item.paperId !== '') params.paperId = item.paperId
        if (item.relId != null && item.relId !== '') params.relId = item.relId
        const res = await this.$Api.Paired.getExamPaperLineageForExpert(params)
        // 拦截器在 HTTP 错误时可能 resolve 出 axios response，这里兼容一层
        const body = res && res.data !== undefined && res.code === undefined && res.status != null
          ? res.data
          : res
        if (!(body && body.code == 200)) {
          this.lineageData = null
          this.lineagePaperKey = null
          this.lineageError = this.lineageErrorMessage(body)
          console.warn('[examPaper/lineageForExpert] 业务失败', {
            params,
            body,
            raw: res
          })
          this.$message.error(this.lineageError)
          return
        }
        this.lineageData = this.mapLineageData(body.data || {})
        this.lineagePaperKey = this.paperLineageKey(item)
      } catch (err) {
        console.log('[examPaper/lineageForExpert] 请求失败', err)
        this.lineageData = null
        this.lineagePaperKey = null
        this.lineageError = this.lineageErrorMessage(null, err)
        this.$message.error(this.lineageError)
      } finally {
        this.lineageLoading = false
      }
    },
    mapLineageData(raw) {
      const data = raw || {}
      // 版本按时间正序；无单独「修改」态（同题号换题 = removed + added/kept）
      const versions = (Array.isArray(data.versions) ? data.versions : [])
        .slice()
        .sort((a, b) => {
          const ta = a && a.versionTime ? String(a.versionTime) : ''
          const tb = b && b.versionTime ? String(b.versionTime) : ''
          if (ta && tb && ta !== tb) return ta < tb ? -1 : 1
          const na = Number(a && a.versionNo) || 0
          const nb = Number(b && b.versionNo) || 0
          return na - nb
        })
        .map((v, idx) => {
          const selected = !!v.selected
          const isFinal = v.isFinal === 1 || v.isFinal === true
          return {
            versionNo: v.versionNo != null ? v.versionNo : idx + 1,
            versionLabel: v.versionLabel || (selected || isFinal ? '当前版本' : ('第 ' + (idx + 1) + ' 版')),
            paperId: v.paperId,
            thirdPaperId: v.thirdPaperId,
            mainTitle: v.mainTitle || '',
            relId: v.relId,
            isFinal: v.isFinal,
            sendStatus: v.sendStatus,
            versionTime: v.versionTime || '',
            questionCount: v.questionCount,
            selected
          }
        })
      const rawQuestions = Array.isArray(data.questions) ? data.questions : []
      // 任务级 specMode / specColumns；已删题细目挂在旧卷 questionId，原样展示
      const lineageSpecMeta = this.dimSpecMetaFromPayload(data, rawQuestions)
      const questions = rawQuestions.map((q, idx) => {
        const status = this.normalizeLineageStatus(q.status)
        const questionId = q.questionId != null ? q.questionId : q.id
        const mapped = {
          displayNo: q.displayNo != null ? q.displayNo : idx + 1,
          status,
          statusLabel: q.statusLabel || this.lineageStatusLabel(status),
          // 已删除题带 versionLabel；终态题为 null
          versionLabel: status === 'removed' ? (q.versionLabel || null) : null,
          paperId: q.paperId,
          questionId,
          thirdQuestionId: q.thirdQuestionId,
          typeName: q.typeName || '',
          partName: q.partName || '',
          stem: q.stem || '',
          answer: q.answer || q.answerParsed || '',
          explanation: this.normalizeExplanation(q.explanation || q.analysis),
          totalScore: q.totalScore,
          noteCount: q.noteCount != null ? Number(q.noteCount) : 0,
          notes: this.mapApiNotes(q.notes || q.mentorNotes || q.noteList),
          knowledgeName: formatDimValue(q.knowledgeName),
          keyAbilityName: formatDimValue(q.keyAbilityName),
          subjectAbilityName: formatDimValue(q.subjectAbilityName),
          requirement: formatDimValue(q.requirement),
          objective: formatDimValue(q.objective),
          customMarks: normalizeCustomMarks(q.customMarks),
          specMode: lineageSpecMeta.specMode,
          markStatus: q.markStatus == null || q.markStatus === '' ? null : Number(q.markStatus)
        }
        if (!mapped.noteCount && mapped.notes.length) mapped.noteCount = mapped.notes.length
        mapped.dimFields = this.buildDimFields(
          mapped,
          lineageSpecMeta.specMode,
          lineageSpecMeta.specColumns
        )
        return mapped
      })
      // 题号升序；同题号时终态（kept/added）在前、已删除在后
      questions.sort((a, b) => {
        const na = Number(a.displayNo) || 0
        const nb = Number(b.displayNo) || 0
        if (na !== nb) return na - nb
        const ra = a.status === 'removed' ? 1 : 0
        const rb = b.status === 'removed' ? 1 : 0
        return ra - rb
      })
      const stats = data.stats || {}
      return {
        taskId: data.taskId,
        taskName: data.taskName || '',
        spxId: data.spxId,
        spxName: data.spxName || '',
        creatorUserId: data.creatorUserId,
        creatorUserName: data.creatorUserName || '',
        selectedPaperId: data.selectedPaperId,
        selectedThirdPaperId: data.selectedThirdPaperId,
        selectedMainTitle: data.selectedMainTitle || '',
        cutoffSendTime: data.cutoffSendTime || null,
        versions,
        stats: {
          versionCount: stats.versionCount,
          currentCount: stats.currentCount,
          mergedCount: stats.mergedCount,
          addedCount: stats.addedCount,
          removedCount: stats.removedCount,
          keptCount: stats.keptCount
        },
        specMode: lineageSpecMeta.specMode,
        specColumns: lineageSpecMeta.specColumns,
        questions
      }
    },
    /** 仅 kept / added / removed；旧「修改」态按无调整兜底 */
    normalizeLineageStatus(status) {
      if (status === 'added' || status === 'removed' || status === 'kept') return status
      return 'kept'
    },
    lineageStatusLabel(status) {
      if (status === 'added') return '新增'
      if (status === 'removed') return '已删除'
      return '无调整'
    },
    lineageCardClass(status) {
      if (status === 'added') return 'is-added'
      if (status === 'removed') return 'is-removed'
      return 'is-kept'
    },
    lineageTagClass(status) {
      if (status === 'added') return 'is-add'
      if (status === 'removed') return 'is-del'
      return 'is-kept'
    },
    hasLineageDims(q) {
      if (!q) return false
      if (Array.isArray(q.dimFields) && q.dimFields.some(dim => dim && dim.value)) return true
      return !!(q.knowledgeName || q.keyAbilityName || q.subjectAbilityName || q.requirement || q.objective ||
        (q.customMarks && Object.keys(q.customMarks).length))
    },
    /**
     * 统一从接口取细目列（与 abilityQuestions / lineage / questionMark 同源约定）：
     * - 优先任务级 specColumns（按 sort 排序，过滤题号与隐藏列）
     * - 非固定表头且无表头时，用本批题目 customMarks 键并集兜底
     * - 固定表头（specMode=0）无表头时保持空列，由 buildDimFields 退回五项
     */
    dimSpecMetaFromPayload(payload, questions) {
      const data = payload && typeof payload === 'object' ? payload : {}
      const list = Array.isArray(questions) ? questions : []
      let specMode = data.specMode
      if (specMode == null && list.some(q => {
        const marks = q && (q.customMarks || (q.raw && q.raw.customMarks))
        return marks && typeof marks === 'object' && !Array.isArray(marks) && Object.keys(marks).length
      })) {
        specMode = 1
      }
      const mode = normalizeSpecMode(specMode)
      const isVisibleName = name => {
        const text = String(name || '').trim()
        return Boolean(text) && text !== '题号' && !isHiddenDimLabel(text)
      }
      let specColumns = []
      if (Array.isArray(data.specColumns) && data.specColumns.length) {
        specColumns = data.specColumns
          .filter(col => col && isVisibleName(col.columnName))
          .slice()
          .sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0))
          .map(col => Object.assign({}, col, {
            columnName: String(col.columnName).trim(),
            builtinKey: col.builtinKey || '',
            valueType: col.valueType,
            options: col.options
          }))
      } else if (mode === 1) {
        // 后端未带回表头时的前端兜底；固定表头不走并集，避免盖住五项
        specColumns = this.unionMarkColumns(list)
      }
      return { specMode: mode, specColumns }
    },
    /** 同一批题目 customMarks 列名并集，保持首次出现顺序 */
    unionMarkColumns(questions) {
      const seen = {}
      const cols = []
      ;(questions || []).forEach(q => {
        if (!q) return
        const marks = normalizeCustomMarks(q.customMarks || (q.raw && q.raw.customMarks) || {})
        Object.keys(marks).forEach(name => {
          const text = String(name || '').trim()
          if (!text || text === '题号' || isHiddenDimLabel(text) || seen[text]) return
          seen[text] = true
          cols.push({ columnName: text })
        })
      })
      return cols
    },
    // 已无调用方：列名统一走 dimSpecMetaFromPayload / unionMarkColumns
    // pickSpecColumns(payload) {
    //   if (!payload || typeof payload !== 'object') return []
    //   return Array.isArray(payload.specColumns) ? payload.specColumns : []
    // },
    // pickCustomOptionColumns(payload) {
    //   if (!payload || typeof payload !== 'object') return []
    //   const options = payload.options || payload.markOptions || {}
    //   const customOptions = options.customOptions
    //   if (!customOptions || typeof customOptions !== 'object' || Array.isArray(customOptions)) return []
    //   return Object.keys(customOptions)
    //     .filter(name => name && String(name).trim() && String(name).trim() !== '题号')
    //     .map(columnName => ({ columnName: String(columnName).trim() }))
    // },
    async fetchExamPaperQuestionList(paperId, taskId) {
      if (paperId == null || paperId === '') {
        this.$message.warning('缺少试卷ID，无法加载题目')
        return []
      }
      const params = { paperId }
      if (taskId != null && taskId !== '') params.taskId = taskId
      try {
        const res = await this.$Api.Paired.getExamPaperQuestionList(params)
        if (!(res && res.code == 200)) {
          this.$message.error((res && res.msg) || '获取试卷题目失败')
          return []
        }
        // 新结构：data.questions；兼容旧结构：data 直接为数组
        const raw = res.data
        const listPayload = Array.isArray(raw) ? { questions: raw } : (raw || {})
        const list = Array.isArray(listPayload.questions) ? listPayload.questions : []
        let markBundle = null
        if (taskId != null && taskId !== '') {
          markBundle = await this.fetchQuestionMarks(taskId, paperId)
        }
        const emptyMeta = { specMode: 0, specColumns: [] }
        let questions = list.map((item, index) => this.mapExamQuestionItem(item, index, emptyMeta))
        if (markBundle && markBundle.questions) {
          questions = this.applyQuestionMarks(questions, markBundle.questions, emptyMeta)
        }
        const columnSource = {
          specMode: markBundle && markBundle.specMode != null ? markBundle.specMode : listPayload.specMode,
          specColumns: (
            markBundle && Array.isArray(markBundle.specColumns) && markBundle.specColumns.length
              ? markBundle.specColumns
              : listPayload.specColumns
          )
        }
        const specMeta = this.dimSpecMetaFromPayload(columnSource, questions)
        this.paperSpecMode = specMeta.specMode
        this.paperSpecColumns = specMeta.specColumns
        return questions.map(q => {
          const next = Object.assign({}, q, { specMode: specMeta.specMode })
          next.dimFields = this.buildDimFields(next, specMeta.specMode, specMeta.specColumns)
          return next
        })
      } catch (err) {
        console.log('[examPaper/question/list] 请求失败', err)
        this.$message.error('获取试卷题目失败')
        return []
      }
    },
    async fetchQuestionMarks(taskId, paperId) {
      try {
        const res = await this.$Api.Paired.getExamComposeQuestionMark({ taskId, paperId })
        if (!(res && res.code == 200)) return null
        const data = res.data || {}
        return {
          specMode: data.specMode,
          specColumns: data.specColumns,
          options: data.options,
          markOptions: data.markOptions || data.options,
          questions: Array.isArray(data.questions) ? data.questions : []
        }
      } catch (err) {
        console.log('[examCompose/questionMark] 请求失败', err)
        return null
      }
    },
    applyQuestionMarks(questions, marks, specMeta) {
      const map = {}
      ;(marks || []).forEach(mark => {
        const id = mark && (mark.paperQuestionId != null ? mark.paperQuestionId : mark.id)
        if (id != null && id !== '') map[String(id)] = mark
      })
      const meta = specMeta || {
        specMode: this.paperSpecMode,
        specColumns: this.paperSpecColumns
      }
      return (questions || []).map(q => {
        const mark = map[String(q.id)]
        if (!mark) {
          return Object.assign({}, q, {
            dimFields: this.buildDimFields(q, meta.specMode, meta.specColumns)
          })
        }
        const pick = (markValue, questionValue) => {
          const fromMark = formatDimValue(markValue)
          if (fromMark) return fromMark
          return formatDimValue(questionValue)
        }
        const next = Object.assign({}, q, {
          knowledgeName: pick(mark.knowledgeName, q.knowledgeName),
          keyAbilityName: pick(mark.keyAbilityName, q.keyAbilityName),
          subjectAbilityName: pick(mark.subjectAbilityName, q.subjectAbilityName),
          requirement: pick(mark.requirement, q.requirement),
          objective: pick(mark.objective, q.objective),
          customMarks: Object.assign({}, q.customMarks || {}, normalizeCustomMarks(mark.customMarks)),
          specMode: normalizeSpecMode(mark.specMode != null ? mark.specMode : meta.specMode),
          markStatus: mark.markStatus == null || mark.markStatus === '' ? q.markStatus : Number(mark.markStatus)
        })
        next.dimFields = this.buildDimFields(next, meta.specMode, meta.specColumns)
        return next
      })
    },
    toggleAnswer() {
      this.showAnswer = !this.showAnswer
    },
    onNoteInput() {
      this.noteDirty = (this.noteDraft || '').trim() !== String(this.confirmedDraft || '').trim()
    },
    questionListForSelect() {
      return this.viewMode === 'ability' ? this.abilityFlatQuestions : this.flatQuestions
    },
    selectQuestion(qid, scroll, force) {
      if (qid == null || qid === '') return
      const meta = this.questionListForSelect().find(q => this.isSameId(q.id, qid))
      if (!meta) return

      if (this.isSameId(qid, this.currentNoteQid) && !force) {
        if (scroll) this.scrollToQuestion(qid)
        return
      }

      if (this.noteDirty && this.currentNoteQid !== '' && this.currentNoteQid != null) {
        this.$confirm(
          '第 ' + this.currentNoteNo + ' 题批注尚未确认。<br>确定：先确认并切换<br>取消：放弃修改并切换',
          '提示',
          {
            confirmButtonText: '确认并切换',
            cancelButtonText: '放弃修改',
            type: 'warning',
            dangerouslyUseHTMLString: true,
            distinguishCancelAndClose: true
          }
        ).then(async () => {
          this.noteSaving = true
          try {
            const saved = await this.persistNote(this.currentNoteQid, this.noteDraft)
            this.confirmedDraft = saved ? String(this.noteDraft || '').trim() : ''
            this.noteDraft = this.confirmedDraft
            this.noteDirty = false
            this.doSelectQuestion(qid, scroll)
          } catch (err) {
            console.log('保存批注失败', err)
            this.$message.error((err && err.message) || '保存批注失败')
          } finally {
            this.noteSaving = false
          }
        }).catch(action => {
          if (action === 'cancel') {
            this.noteDirty = false
            this.doSelectQuestion(qid, scroll)
          }
        })
        return
      }

      this.doSelectQuestion(qid, scroll)
    },
    doSelectQuestion(qid, scroll) {
      const meta = this.questionListForSelect().find(q => this.isSameId(q.id, qid))
      if (!meta) return
      this.currentNoteQid = qid
      this.currentNoteNo = meta.no
      const val = this.getMyNoteContent(qid)
      this.confirmedDraft = val
      this.noteDraft = val
      this.noteDirty = false
      if (scroll) this.scrollToQuestion(qid)
    },
    scrollToQuestion(qid) {
      this.$nextTick(() => {
        const el = document.getElementById(this.questionDomId(qid))
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    toggleTask(taskId) {
      const opening = this.collapsedTasks[taskId] !== false
      const next = {}
      this.groupedPapers.forEach(group => {
        next[group.id] = String(group.id) === String(taskId) ? !opening : true
      })
      this.collapsedTasks = next
      if (!opening) return
      const group = this.groupedPapers.find(item => String(item.id) === String(taskId))
      const first = group && group.papers && group.papers[0]
      if (first) this.selectPaper(first)
    },
    hasVersion(item) {
      const value = Number(item && item.isFinal)
      return value === 0 || value === 1 || value === 2
    },
    isFinalPaper(item) {
      return Number(item && item.isFinal) === 1
    },
    isProcessPaper(item) {
      return Number(item && item.isFinal) === 2
    },
    versionLabel(item) {
      const value = Number(item && item.isFinal)
      if (value === 1) return '终版'
      if (value === 2) return '过程版本'
      if (value === 0) return '未终版'
      return ''
    },
    versionClass(item) {
      const value = Number(item && item.isFinal)
      if (value === 1) return 'is-final'
      if (value === 2) return 'is-process'
      return 'is-draft'
    },
    reviewCode(item) {
      if (!this.isFinalPaper(item)) return null
      const value = Number(item && item.reviewStatus)
      if (value === 1 || value === 2) return value
      return 0
    },
    reviewLabel(item) {
      const code = this.reviewCode(item)
      if (code === 1) return '已确认可行'
      if (code === 2) return '不可行'
      return '待确认'
    },
    reviewClass(item) {
      const code = this.reviewCode(item)
      if (code === 1) return 'is-approved'
      if (code === 2) return 'is-rejected'
      return 'is-pending'
    },
    reviewKey(item) {
      const code = this.reviewCode(item)
      if (code === 1) return 'approved'
      if (code === 2) return 'rejected'
      return 'pending'
    },
    reviewDesc(item) {
      const code = this.reviewCode(item)
      if (code === 1) {
        return '已确认可行，学院端可上传答题卡。' + (item.reviewTime ? '确认时间：' + item.reviewTime : '')
      }
      if (code === 2) {
        return '已判定不可行，学院端暂不能上传答题卡。' + (item.reviewTime ? '处理时间：' + item.reviewTime : '')
      }
      return '终版待确认。确认可行后，学院端可上传答题卡。'
    },
    reviewLocked(item) {
      const code = this.reviewCode(item)
      return code === 1 || code === 2
    },
    taskGoalText(key) {
      const text = String((this.taskGoal && this.taskGoal[key]) || '').trim()
      return text || '暂无'
    },
    normalizeTaskGoal(raw) {
      const goal = raw && typeof raw === 'object' ? raw : {}
      return {
        overall: goal.overall || '',
        knowledge: goal.knowledge || '',
        ability: goal.ability || '',
        focus: goal.focus || ''
      }
    },
    openTaskGoal() {
      if (!this.activePaper) return
      this.taskGoalVisible = true
      this.loadTaskGoal()
    },
    loadTaskGoal() {
      const paper = this.activePaper || {}
      this.taskGoalLoading = false
      this.taskGoal = this.normalizeTaskGoal(paper.taskGoal || paper.goal)
    },
    confirmFeasible() {
      if (!this.activePaper || this.reviewSaving || this.reviewLocked(this.activePaper)) return
      this.$confirm('确认本终版试卷可行？确认后不可再改，学院端可上传答题卡。', '提示', {
        confirmButtonText: '确认可行',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.submitFeasibility(1, '')
      }).catch(() => {})
    },
    openReject() {
      if (!this.activePaper || this.reviewSaving || this.reviewLocked(this.activePaper)) return
      this.rejectReason = ''
      this.rejectVisible = true
    },
    submitReject() {
      const reason = String(this.rejectReason || '').trim()
      if (!reason) {
        this.$message.warning('请填写不可行原因')
        return
      }
      if (reason.length > 500) {
        this.$message.warning('不可行原因不能超过500字')
        return
      }
      this.submitFeasibility(2, reason)
    },
    async submitFeasibility(status, reason) {
      const paper = this.activePaper
      if (!paper || this.reviewLocked(paper)) return
      if (paper.relId == null || paper.relId === '') {
        this.$message.warning('缺少绑定ID，暂时无法提交确认')
        return
      }
      this.reviewSaving = true
      try {
        const query = { relId: paper.relId, status }
        const body = status === 2 ? { reason } : {}
        const res = await this.$Api.Paired.submitExamPaperFeasibility(query, body)
        if (!(res && (res.code == 200 || res.code == 0))) {
          this.$message.error((res && res.msg) || '可行性确认失败')
          return
        }
        this.patchPaperReview(paper.id, status, reason)
        this.rejectVisible = false
        this.$message.success(status === 1 ? '已确认可行' : '已判定不可行')
      } catch (err) {
        console.log('可行性确认失败', err)
        this.$message.error('可行性确认失败')
      } finally {
        this.reviewSaving = false
      }
    },
    patchPaperReview(paperId, status, reason) {
      const pad = n => (n < 10 ? '0' + n : String(n))
      const now = new Date()
      const reviewTime = now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate()) + ' ' + pad(now.getHours()) + ':' + pad(now.getMinutes())
      const patch = {
        reviewStatus: status,
        reviewReason: status === 2 ? reason : '',
        reviewUserName: this.currentAuthor,
        reviewTime
      }
      this.allPapers = this.allPapers.map(item => (this.isSameId(item.id, paperId) ? Object.assign({}, item, patch) : item))
      if (this.activePaper && this.isSameId(this.activePaper.id, paperId)) {
        this.activePaper = Object.assign({}, this.activePaper, patch)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.thin-scroll() {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.28) transparent;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.28);
    border-radius: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.paper-manage {
  --primary: #4a90e2;
  --primary-dark: #3a7bc8;
  --primary-light: #e8f3ff;
  --primary-blue: #2b7de9;
  --text: #333;
  --text-secondary: #666;
  --text-muted: #999;
  --bg: #fff;
  --card: #fff;
  --border: #e8ecf0;
  --max: 1200px;
  --radius: 8px;
  --shadow: 0 1px 8px rgba(0, 0, 0, 0.06);

  box-sizing: border-box;
  height: auto;
  overflow: visible;
  display: flex;
  flex-direction: column;
  padding: 8px 0 16px;
  background: var(--bg);
  color: var(--text);
  font-size: 14px;
  line-height: 1.5;
  font-family: "PingFang SC", "Microsoft YaHei", "Helvetica Neue", sans-serif;

  * { box-sizing: border-box; }
}

.wrap {
  width: 100%;
  max-width: var(--max);
  margin: 0 auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  // padding: 0 16px;
}

.page-hd {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.stat-chips {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  height: 44px;
}

.stat-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 44px;
  box-sizing: border-box;
  background: #fff;
  border-radius: 8px;
  box-shadow: var(--shadow);
  padding: 0 12px;
  white-space: nowrap;

  .num {
    font-size: 18px;
    font-weight: 700;
    color: var(--primary);
    line-height: 1;
  }

  .label {
    font-size: 12px;
    color: var(--text-muted);
  }
}

.layout {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 14px;
  align-items: stretch;
}

.panel {
  background: var(--card);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.view-mode-tabs {
  display: flex;
  align-items: stretch;
  gap: 0;
  flex: 1;
  min-width: 0;
  height: 44px;
  box-sizing: border-box;
  margin-bottom: 0;
  padding: 0 4px;
  background: #fff;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  border-bottom: 1px solid var(--border);
  flex-shrink: 1;
}

.view-mode-tab {
  position: relative;
  box-sizing: border-box;
  height: 100%;
  padding: 0 20px;
  font-size: 14px;
  color: var(--text-secondary);
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  background: none;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.15s, border-color 0.15s;

  &:hover { color: var(--primary); }

  &.is-active {
    color: var(--primary);
    font-weight: 600;
    border-bottom-color: var(--primary);
  }
}

.page-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  padding: 10px 14px;
  background: #fff;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  flex-shrink: 0;
}

.filter-select,
.search-input {
  height: 34px;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0 12px;
  font-size: 13px;
  font-family: inherit;
  color: var(--text-secondary);
  background: #fafbfc;
  outline: none;

  &:focus {
    border-color: #a8c8f0;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
  }
}

.filter-select {
  width: auto;
  min-width: 168px;
  max-width: 240px;
}

.search-input {
  margin-left: auto;
  width: 280px;
  max-width: 100%;
}

.ability-search-bar {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 1;
  box-sizing: border-box;
  height: 48px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  background: #fff;
  border-bottom: 1px solid var(--border);

  .search-input {
    display: block;
    width: 100%;
    height: 32px;
    margin-left: 0;
    box-sizing: border-box;
  }
}

.ability-head-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-top: 8px;
  font-size: 13px;
  color: var(--text-secondary);
  justify-content: center;

  strong {
    color: var(--text);
    font-weight: 600;
  }
}

.ability-source-block {
  margin: 0 0 22px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e8ecf0;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
}

.ability-source-hd {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px 16px;
  margin: 0;
  padding: 12px 14px 12px 16px;
  border-radius: 8px;
  background: #eef5ff;
  border: 1px solid #c5daf5;
  border-left: 4px solid var(--primary);
}

.ability-source-main {
  min-width: 0;
  flex: 1;
}

.ability-source-hd + .q-item {
  margin-top: 14px;
}

.ability-source-title,
.ability-source-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 20px;
  row-gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2a37;
  line-height: 1.5;
}

.ability-source-meta {
  margin-top: 4px;
}

.ability-field-label {
  color: #3d5a80;
  font-weight: 700;
}

.ability-source-count {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid #c5daf5;
  background: #fff;
  font-size: 13px;
  color: var(--primary);
  font-weight: 600;
  line-height: 1;
  box-sizing: border-box;
}

.q-source-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin: 6px 0 10px;
  font-size: 12px;
  color: var(--text-muted);
}

.q-source-item strong {
  color: var(--text-secondary);
  font-weight: 600;
}

.catalog-panel {
  position: relative;
  min-height: 0;
  overflow: hidden;
}

.catalog-list {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow-x: hidden;
  overflow-y: auto;
  .thin-scroll();

  &.is-with-search {
    top: 48px;
  }
}

.catalog-group {
  border-bottom: 1px solid #eef1f5;

  &:last-child { border-bottom: none; }
}

.catalog-group-hd-wrap {
  background: #f7f9fc;

  &:hover { background: #eef4fb; }
}

.catalog-group-hd {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: #f7f9fc;
  text-align: left;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: #eef4fb; }
}

.catalog-group-hd-wrap .catalog-group-hd {
  padding: 10px 12px 0;
  background: transparent;

  &:hover { background: transparent; }
}

.catalog-group-arrow {
  margin-top: 2px;
  color: var(--text-muted);
  transition: transform 0.15s;
}

.catalog-group.is-collapsed .catalog-group-arrow {
  transform: rotate(-90deg);
}

.catalog-group-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.catalog-group-title {
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: #1a3a5c;
  line-height: 1.4;
}

.catalog-group-subrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 12px 10px 28px;
  min-width: 0;
}

.catalog-group-meta {
  margin-top: 2px;
  font-size: 11px;
  color: var(--text-muted);
  min-width: 0;
}

.catalog-group-subrow .catalog-group-meta {
  margin-top: 0;
}

.catalog-item {
  padding: 12px 14px 12px 28px;
}

.item-title-row {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin-bottom: 6px;

  .item-title {
    margin-bottom: 0;
    flex: 1;
    min-width: 0;
  }
}

.version-tag,
.review-tag {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 20px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
}

.version-tag.is-process,
.review-tag.is-pending {
  color: #c47a12;
  background: #fff7e8;
  border: 1px solid #f0d4a0;
}

.version-tag.is-final,
.review-tag.is-approved {
  color: #1f8a4c;
  background: #eaf7ef;
  border: 1px solid #b7e0c6;
}

.version-tag.is-draft {
  color: #666;
  background: #f2f3f5;
}

.review-tag.is-rejected {
  color: #c62828;
  background: #fdeeee;
  border: 1px solid #f0c7c7;
}

.paper-review-bar {
  margin-top: 14px;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #f0d4a0;
  background: #fffaf0;
  min-width: 0;
  max-width: 100%;

  &.is-approved {
    background: #f7fcf9;
    border-color: #b7e0c6;
  }

  &.is-rejected {
    background: #fff8f8;
    border-color: #f0c7c7;
  }
}

.paper-review-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
}

.paper-review-desc,
.paper-review-reason {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
  overflow-wrap: anywhere;
  word-break: break-all;
}

.paper-review-reason {
  margin-top: 4px;
  color: #a33b3b;
}

.paper-review-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.btn-approve,
.btn-reject {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
}

.btn-approve {
  border: none;
  background: #1f8a4c;
  color: #fff;

  &:hover:not(:disabled) { background: #187a42; }

  &:disabled {
    background: #b7e0c6;
    cursor: default;
  }
}

.btn-reject {
  border: 1px solid #e0a0a0;
  background: #fff;
  color: #c62828;

  &:hover:not(:disabled) { background: #fff5f5; }

  &:disabled {
    color: #e0b4b4;
    border-color: #f0d8d8;
    cursor: default;
  }
}

.q-note-display-version::before {
  content: "批注版本：";
}

.reject-reason-tip {
  margin: 0 0 10px;
  color: #666;
  font-size: 13px;
  line-height: 1.5;
}

.catalog-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 12px 14px 12px 28px;
  border: none;
  border-bottom: 1px solid #f3f5f8;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s;
  font-family: inherit;

  &:hover { background: #f7faff; }

  &.is-active {
    background: var(--primary-light);
    position: relative;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: var(--primary);
    }

    .item-title { color: var(--primary); }
  }
}

.item-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  line-height: 1.4;
  margin-bottom: 6px;
}

.item-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: var(--text-muted);
}

.meta-row {
  display: flex;
  gap: 4px;
  line-height: 1.4;
}

.meta-label {
  color: var(--text-muted);
  flex-shrink: 0;
}

.meta-value {
  color: var(--text-secondary);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty,
.detail-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  text-align: center;
  color: var(--text-muted);
  font-size: 13px;
  min-height: 360px;

  svg {
    width: 64px;
    height: 64px;
    margin: 0 auto 10px;
    opacity: 0.85;
  }
}

.detail-placeholder svg {
  width: 80px;
  height: 80px;
  margin-bottom: 12px;
  opacity: 0.9;
}

.detail-panel {
  min-height: 0;
  overflow: hidden;
}

.detail-tabs {
  display: flex;
  align-items: stretch;
  gap: 0;
  padding: 0 12px;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(180deg, #f8fbff 0%, #fff 100%);
  flex-shrink: 0;

  &.is-ability-version-tabs {
    box-sizing: border-box;
    height: 48px;

    .detail-tab {
      height: 100%;
    }
  }
}

.task-goal-btn {
  margin-left: auto;
  align-self: center;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #606266;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    color: var(--primary);
    border-color: #b3d4f5;
    background: #f5f9ff;
  }
}

.task-goal-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 80px;
}

.task-goal-block {
  padding: 12px 14px;
  background: #f7f8fa;
  border: 1px solid #eef0f3;
  border-radius: 6px;
}

.task-goal-label {
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  line-height: 22px;
}

.task-goal-text {
  font-size: 13px;
  line-height: 1.7;
  color: #606266;
  white-space: pre-wrap;
  word-break: break-word;

  &.is-empty {
    color: #c0c4cc;
  }
}

.detail-tab {
  position: relative;
  height: 42px;
  padding: 0 16px;
  font-size: 14px;
  color: var(--text-secondary);
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  background: none;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.15s, border-color 0.15s;

  &:hover { color: var(--primary); }

  &.is-active {
    color: var(--primary);
    font-weight: 600;
    border-bottom-color: var(--primary);
  }
}

.detail-scroll {
  overflow: visible;
}

.lineage-scroll {
  overflow-x: hidden;
  overflow-y: auto;
  max-height: calc(100vh - 280px);
  min-height: 360px;
  .thin-scroll();
}

.lineage-wrap {
  padding: 14px 16px 24px;
}

.lineage-summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  background: #f7faff;
  border: 1px solid #e3edf8;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 13px;
  color: var(--text-secondary);

  strong {
    color: var(--text);
    font-weight: 600;
  }
}

.lineage-summary-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
  line-height: 1.4;
}

.lineage-summary-meta,
.lineage-summary-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  align-items: center;
}

.lineage-summary-stats {
  .is-add strong {
    color: #16a34a;
  }

  .is-del strong {
    color: #dc2626;
  }
}

.lineage-timeline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 4px;
  margin-bottom: 18px;
}

.lineage-node {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  min-width: 120px;

  &.is-current {
    border-color: var(--primary);
    background: var(--primary-light);
  }
}

.lineage-node-label {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
}

.lineage-node-time {
  font-size: 12px;
  font-weight: 400;
  color: var(--text-muted);
}

.lineage-node-meta {
  font-size: 12px;
  color: var(--text-muted);
  line-height: 1.45;
}

.lineage-arrow {
  color: #c0cad6;
  font-size: 16px;
  padding: 0 2px;
  flex-shrink: 0;
}

.lineage-section {
  margin-top: 4px;
  margin-bottom: 18px;
}

.lineage-section-hd {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eef1f5;
}

.lineage-section-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
}

.lineage-count {
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;

  &.is-add {
    background: #eaf7ef;
    color: #1f8a4c;
  }

  &.is-del {
    background: #fdeeee;
    color: #c62828;
  }
}

.lineage-q {
  position: relative;
  border: 1px solid #eef1f5;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 10px;
  background: #fff;

  &:last-child { margin-bottom: 0; }

  &.is-kept {
    border-color: #e3edf8;
    background: #fff;
  }

  &.is-added {
    border-color: #c8e6d3;
    background: #f7fcf9;
  }

  &.is-removed {
    border-color: #f0c7c7;
    background: #fff8f8;

    .lineage-q-stem {
      color: #888;
      text-decoration: line-through;
      text-decoration-color: #d0a0a0;
    }

    .lineage-q-verno {
      color: #c62828;
      background: #fdeeee;
      border-color: #f0c7c7;
    }
  }

  .change-tag {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1;
  }
}

.lineage-q-hd {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding-right: 64px;
}

.lineage-q-no {
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
}

.lineage-q-verno {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-muted);
  background: #f3f5f8;
  border: 1px solid #e4e9ef;
  border-radius: 4px;
  padding: 1px 6px;
  line-height: 1.4;
}

.lineage-q-type {
  font-size: 12px;
  color: var(--text-muted);
}

.lineage-q-stem {
  font-size: 14px;
  color: #333;
  line-height: 1.7;
  margin-bottom: 8px;
  overflow-wrap: anywhere;
  word-break: break-word;

  /deep/ img {
    max-width: 100%;
    height: auto;
    vertical-align: middle;
  }

  /deep/ p {
    margin: 0 0 6px;
  }
}

.lineage-q.is-added .lineage-q-verno {
  color: #1f8a4c;
  background: #eaf7ef;
  border-color: #b7e0c6;
}

.change-tag {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;

  &.is-kept {
    background: #eef4fb;
    color: #3a7bc8;
    border: 1px solid #c5d8f0;
  }

  &.is-add {
    background: #eaf7ef;
    color: #1f8a4c;
    border: 1px solid #b7e0c6;
  }

  &.is-del {
    background: #fdeeee;
    color: #c62828;
    border: 1px solid #f0c7c7;
  }
}

.lineage-q-dims {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin: 4px 0 8px;
  font-size: 12px;
  color: var(--text-secondary);
}

.lineage-dims-block {
  margin: 8px 0 4px;
}

.lineage-dims-hd {
  cursor: default;
  margin-bottom: 6px;

  .q-dims-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
}

.lineage-dim {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  max-width: 100%;

  span {
    color: var(--text-muted);
    flex-shrink: 0;

    &::after {
      content: "：";
    }
  }
}

.lineage-mentor-notes {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e8ecf0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.lineage-mentor-title {
  font-size: 12px;
  font-weight: 600;
  color: #b45309;
}

.lineage-empty-hint {
  font-size: 13px;
  color: var(--text-muted);
  padding: 8px 0;
}

.paper-detail-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 188px;
  gap: 10px;
  align-items: stretch;
  padding: 8px;
  overflow: visible;
}

.paper-card-slot {
  position: relative;
  min-width: 0;
  min-height: 0;
}

.detail-aside {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: auto;
  min-width: 0;
  min-height: calc(100vh - 280px);
  overflow: visible;
}

.side-card {
  background: #fafbfc;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  padding: 14px 12px;
  flex: 0 0 auto;
  min-width: 0;
  overflow: visible;
}

.side-card-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #333;
  }
}

.btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
  font-family: inherit;

  &:hover {
    border-color: var(--primary-blue);
    color: var(--primary-blue);
  }

  &.is-on {
    border-color: var(--primary-blue);
    color: var(--primary-blue);
    background: #f0f7ff;
  }
}

.side-card-hd .btn-outline {
  flex-shrink: 0;
  padding: 4px 8px;
  font-size: 12px;
  white-space: nowrap;
}

.toc-list {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
  overflow: visible;

  &.toc-list-grouped {
    display: block;
    max-height: 320px;
    overflow-x: hidden;
    overflow-y: auto;
    .thin-scroll();
  }
}

.toc-person-group {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #e0e0e0;

  &:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
}

.toc-person-name {
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  line-height: 1.4;
}

.toc-person-items {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
}

.toc-item {
  width: 100%;
  height: 28px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  font-family: inherit;
  padding: 0;

  &:hover {
    border-color: var(--primary-blue);
    color: var(--primary-blue);
  }

  &.is-current {
    border-color: #2b7de9;
    background: #e8f2fd;
    color: #2b7de9;
    font-weight: 600;
    box-shadow: none;
  }

  &.has-note {
    border-color: #e0a84a;
    background: #fff8eb;
    color: #b45309;

    &::after {
      content: "";
      position: absolute;
      top: 3px;
      right: 3px;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: #e0a84a;
    }
  }

  &.has-note.is-current {
    background: #fff8eb;
    color: #2b7de9;
    border-color: #2b7de9;
    box-shadow: inset 0 0 0 1px #2b7de9;
  }
}

.side-stat {
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
  font-size: 12px;
  color: var(--text-muted);
  line-height: 1.6;
}

.paper-card {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  padding: 16px 18px 22px;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  .thin-scroll();
}

.paper-head {
  text-align: center;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 20px;

  h1 {
    margin: 0 0 10px;
    font-size: 18px;
    font-weight: 600;
    color: #222;
    line-height: 1.4;
  }
}

.paper-head-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 4px;
}

.paper-head-info {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f5f8fc;
  border: 1px solid #e6eef8;
}

.paper-head-chip {
  min-width: 0;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 500;
  color: #4a5d73;
  line-height: 1.3;
}

.paper-head-dot {
  flex-shrink: 0;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #b7c6d8;
}

.paper-tag {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  background: #f0f3f7;
  border: 1px solid #e5ebf2;
  border-radius: 999px;
  font-size: 12px;
  color: #66788a;
  line-height: 1;
}

.section-title {
  margin: 24px 0 14px;
  padding-left: 10px;
  border-left: 3px solid var(--primary-blue);
  font-size: 15px;
  font-weight: 600;
  color: #222;

  &:first-of-type { margin-top: 0; }
}

.q-item {
  margin-bottom: 16px;
  padding: 10px 12px;
  border: 1.5px solid transparent;
  border-radius: 10px;
  scroll-margin-top: 12px;
  cursor: pointer;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover:not(.is-active) {
    border-color: #e4e7ec;
  }

  &.is-active {
    background: transparent;
    border-color: #2b7de9;
    box-shadow: 0 0 0 3px rgba(43, 125, 233, 0.18);
  }
}

.q-stem {
  display: flow-root;
  font-size: 14px;
  color: #333;
  line-height: 1.75;
  margin-bottom: 10px;
}

.q-no,
.q-score {
  float: left;
}

.q-no {
  font-weight: 600;
  margin-right: 4px;
}

.q-score {
  color: var(--text-muted);
  font-size: 12px;
  margin-right: 4px;
}

.q-stem-html {
  display: block;
  margin-top: 0;

  /deep/ .qml-stem {
    display: block;
  }

  /deep/ .qml-stem > p {
    margin: 0 0 8px;
    line-height: 1.75;
  }

  /deep/ .qml-stem > p:last-child {
    margin-bottom: 0;
  }

  /* 选项：对齐原型纵向列表 .q-options */
  /deep/ .qml-og,
  /deep/ div.qml-og {
    margin: 0 0 8px;
    padding: 0;
  }

  /deep/ table.qml-og {
    width: 100%;
    border-collapse: collapse;
    margin: 0;
  }

  /deep/ table.qml-og,
  /deep/ table.qml-og > tbody,
  /deep/ table.qml-og > tbody > tr {
    display: block;
    width: 100%;
  }

  /deep/ table.qml-og td {
    display: block;
    width: 100% !important;
    padding: 4px 0;
    padding-left: 24px;
    color: #444;
    line-height: 1.7;
    vertical-align: top;
  }

  /deep/ .qml-op {
    display: inline;
  }

  /deep/ .xkw-math-img,
  /deep/ img {
    max-width: 100%;
    height: auto;
    vertical-align: middle;
  }

  /deep/ .qml-bk {
    display: inline-block;
    min-width: 4em;
    border-bottom: 1px solid #333;
    text-decoration: none !important;
    color: transparent;
  }
}

.q-dims {
  margin: 8px 0 12px;
  padding: 8px 12px;
  background: #f7f8fa;
  border: 1px solid #eee;
  border-radius: 4px;

  &:not(.is-collapsed) {
    padding: 10px 14px 12px;
    background: #f3faf6;
    border-color: #d7f0e1;
    border-radius: 8px;
  }
}

.q-dims-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}

.q-dims-title {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #3d9a62;
}

.q-dims-hint {
  margin-left: 4px;
  font-size: 12px;
  font-weight: 400;
  color: #b0b0b0;
}

.q-dims-arrow {
  font-size: 12px;
  transition: transform 0.2s;
}

.q-dims.is-collapsed .q-dims-arrow {
  transform: rotate(-90deg);
}

.q-dims-status {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 500;
  color: #8a8a8a;

  &.is-1 { color: #d48806; }
  &.is-2 { color: #3d9a62; }
}

.q-dims-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 20px;
  margin-top: 12px;
}

.q-dim-label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  color: #666;
}

.q-dim-box {
  min-height: 32px;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  font-size: 13px;
  line-height: 20px;
  color: #333;
  word-break: break-word;
}

.q-kp {
  display: inline-block;
  padding: 1px 8px;
  background: #e8f2fd;
  color: #2b7de9;
  border-radius: 3px;
  font-size: 12px;
}

.q-extra {
  margin-top: 10px;
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #eef2f7;
  font-size: 13px;
  color: #333;
}

.q-extra-row {
  margin-bottom: 8px;
  line-height: 1.7;

  &:last-child { margin-bottom: 0; }
}

.q-extra-label {
  color: var(--primary-blue);
  font-weight: 600;
  margin-right: 6px;
}

.q-extra-html {
  /deep/ .qml-answer,
  /deep/ .qml-an-sq,
  /deep/ .qml-an {
    display: inline;
  }

  /deep/ .xkw-math-img,
  /deep/ img {
    max-width: 100%;
    height: auto;
    vertical-align: middle;
  }

  &.is-block {
    display: block;
    margin-top: 4px;

    /deep/ .qml-explanation,
    /deep/ .qml-seg {
      display: block;
      margin: 0 0 8px;
    }

    /deep/ .qml-seg[seg-name="分析"] {
      display: none !important;
    }

    /deep/ .qml-seg:last-child {
      margin-bottom: 0;
    }

    /deep/ .q-extra-label {
      color: var(--primary-blue);
      font-weight: 600;
      margin-right: 6px;
    }

    /deep/ p {
      margin: 0 0 6px;
      line-height: 1.7;
    }
  }
}

.q-note-display {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.q-note-display-head {
  font-size: 12px;
  font-weight: 600;
  color: #8a6d3b;
  padding-left: 2px;
}

.q-note-item {
  padding: 12px 14px;
  background: #fffbf5;
  border: 1px solid #f0e0c8;
  border-left: 3px solid #e0a84a;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.7;
  color: #5c4a32;
}

.q-note-display-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #ead9bc;
  font-size: 12px;
  color: var(--text-muted);
}

.q-note-display-author {
  font-weight: 500;
  color: #8a6d3b;

  &::before {
    content: "批注人：";
    font-weight: 400;
    color: var(--text-muted);
  }
}

.q-note-display-time::before {
  content: "批注时间：";
}

.q-note-display-text {
  color: #5c4a32;
  word-break: break-word;
  line-height: 1.65;

  /deep/ .katex {
    font-size: 1em;
  }
}

.note-panel {
  background: #fffbf5;
  border: 1px solid #f0e0c8;
  border-radius: 4px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow: visible;
}

.note-scope-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 12px;
  border: 1px solid #e8d9c0;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  flex-shrink: 0;
}

.note-scope-tab {
  flex: 1;
  height: 34px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: #8a7355;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s, color 0.15s;

  & + .note-scope-tab {
    border-left: 1px solid #e8d9c0;
  }

  &:hover {
    background: #fff8ee;
  }

  &.is-active {
    background: #e8a23a;
    color: #fff;
  }
}

.note-panel-editor {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.note-panel-paper {
  gap: 10px;
}

.paper-review-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 2px;
  flex: 0 1 auto;
}

.paper-review-empty {
  padding: 16px 8px;
  text-align: center;
  font-size: 12px;
  color: var(--text-muted);
  background: #fff;
  border: 1px dashed #ead9bc;
  border-radius: 4px;
}

.paper-review-item {
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #f0e0c8;
  border-left: 3px solid #e0a84a;
  border-radius: 6px;

  &.is-mine {
    border-left-color: #2e7d32;
  }

  &.is-editing {
    border-color: #e0a84a;
    background: #fffbf5;
  }
}

.paper-review-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  margin-bottom: 6px;
  font-size: 12px;
  color: var(--text-muted);
}

.paper-review-author {
  font-weight: 500;
  color: #8a6d3b;
}

.paper-review-text {
  font-size: 13px;
  line-height: 1.65;
  color: #5c4a32;
  word-break: break-word;

  /deep/ .katex {
    font-size: 1em;
  }
}

.paper-review-item-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.paper-review-compose {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding-top: 4px;
  border-top: 1px dashed #ead9bc;
}

.paper-review-compose-input,
.paper-review-edit-input {
  min-height: 120px;
  flex: 1 1 auto;
}

.btn-text {
  border: none;
  background: transparent;
  padding: 0 2px;
  font-size: 12px;
  color: #c4882a;
  cursor: pointer;
  font-family: inherit;

  &:hover { color: #a66e16; }

  &.is-danger { color: #c45656; }

  &.is-danger:hover { color: #a33d3d; }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.note-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.note-panel-title {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.note-panel-status {
  font-size: 11px;
  color: var(--text-muted);

  &.is-saved { color: #2e7d32; }
}

.note-panel-input {
  flex: 1 1 auto;
  width: 100%;
  height: auto;
  min-height: 220px;
  border: 1px solid #e8d9c0;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.65;
  color: #333;
  background: #fff;
  resize: none;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #e0a84a;
    box-shadow: 0 0 0 2px rgba(224, 168, 74, 0.15);
  }

  &::placeholder { color: #c0b09a; }

  &:disabled {
    background: #faf8f5;
    cursor: not-allowed;
  }
}

.note-panel-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  flex-shrink: 0;

  .btn-outline:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-confirm {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border: none;
  border-radius: 6px;
  background: #e0a84a;
  font-size: 13px;
  color: #fff;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: #d4962f; }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

@media (max-width: 1100px) {
  .paper-detail-layout { grid-template-columns: minmax(0, 1fr); }

  .detail-aside {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: stretch;
    min-height: 0;
    overflow: hidden;
  }

  .note-panel-input { min-height: 220px; }
}

@media (max-width: 900px) {
  .paper-manage {
    height: auto;
    overflow: visible;
  }

  .layout { grid-template-columns: 1fr; }

  .catalog-panel { height: auto; overflow: visible; }

  .catalog-list,
  .paper-card {
    position: static;
    max-height: 420px;
  }

  .stat-chips { display: none; }

  .paper-detail-layout { grid-template-columns: 1fr; }

  .detail-aside {
    grid-template-columns: 1fr;
    position: static;
    max-height: none;
  }

  .paper-card { padding: 16px 14px 22px; }
}
</style>

<style lang="less">
.task-goal-dialog {
  border-radius: 8px;
  overflow: hidden;

  .el-dialog__header {
    padding: 16px 20px 14px;
    border-bottom: 1px solid #f0f2f5;
  }

  .el-dialog__title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    line-height: 24px;
  }

  .el-dialog__headerbtn {
    top: 18px;
    right: 16px;
  }

  .el-dialog__body {
    padding: 16px 20px 4px;
    max-height: 62vh;
    overflow-y: auto;
  }

  .el-dialog__footer {
    padding: 12px 20px 16px;
    border-top: none;
  }

  .el-button--primary {
    min-width: 72px;
    border-radius: 4px;
  }
}
</style>
